"use client"

import type React from "react"
import {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from "react"
import {
    FiCalendar,
    FiList,
    FiGrid,
    FiClock,
    FiUser,
    FiChevronLeft,
    FiChevronRight,
    FiCheck,
    FiX,
    FiPlus,
    FiFilter,
    FiRefreshCw,
    FiSearch,
    FiEdit,
    FiMap,
    FiLayers,
    FiMaximize2,
    FiMinimize2,
} from "react-icons/fi"
import { FaCalculator, FaSort, FaSortUp, FaSortDown, FaFileExcel } from "react-icons/fa"
import axiosInstance from "../../libs/axios"
import { PaginationType, Pagination as PaginationUtils } from "../../utils/pagination.utils"
import { SearchableSelect, SelectWithSearch, type SelectOption } from "../../utils/select-with-search"
import { useAuth } from "../../provider/auth.provider"
import { ExcelDownload } from "../../utils/excel.utils"
import { getAssociationDisplayLabel } from "../../utils/association.utils"
import {
    ATTENDANCE_STICKY_COLUMNS,
    DensityToggle,
    buildStickyLayout,
    useAttendanceDensity,
} from "./table-density"
import { LeaveQuotaHint } from "./leave-quota-hint"
import AttendanceLocationDetails from "./location_details"
import {
    ATTENDANCE_LEAVE_TYPE_OPTIONS,
    DEFAULT_ATTENDANCE_LEAVE_TYPE,
    getAttendanceLeaveTypeLabel,
    getDisplayHRStatusBadgeClasses,
    getDisplayHRStatusLabel,
    getManualMarkDateError,
    getManualMarkMaxDate,
    getManualMarkSettings,
} from "../../libs/attendance"

import { useUrlFilters } from "../../hooks/useUrlFilters";

/** Query params a dashboard tile may pre-apply to this list. */
const URL_FILTER_KEYS = ["search", "work_status", "hr_status", "marking_type", "user_id", "user_name", "project_id", "project_name", "department_id", "department_name", "role_id", "role_name", "association_type", "attendance_date_from", "attendance_date_to"];

interface AttendanceUserProject {
    id: number
    user_id: number
    project_id: number
    project?: {
        id: number
        project_name: string
        client_project_id?: number | null
        client_project?: {
            id?: number
            client_project_name?: string
        } | null
    } | null
}

interface AttendanceUser {
    id: number
    first_name: string
    last_name: string
    email: string
    username?: string | null
    employee_code?: string | null
    device_version?: string | null
    association_type?: string | null
    designation?: {
        id?: number
        name?: string
    } | null
    role?: {
        id?: number
        name?: string
    } | null
    department?: {
        id?: number
        name?: string
    } | null
    user_projects?: AttendanceUserProject[] | null
    state?: {
        id?: number
        state_name?: string
        circle_name?: string | null
    } | null
    district?: { id?: number; name?: string } | string | null
    reporting_latitude?: string | number | null
    reporting_longitude?: string | number | null
    project_manager?: {
        id?: number
        first_name?: string
        last_name?: string
        email?: string
        employee_code?: string | null
    } | null
}

interface AttendanceRecord {
    id: number
    user_id: number
    attendance_date: string
    check_in_time: string | null
    check_out_time: string | null
    check_in_latitude: number | null
    check_in_longitude: number | null
    check_out_latitude: number | null
    check_out_longitude: number | null
    check_in_address: string | null
    check_out_address: string | null
    check_in_selfie: string | null
    check_out_selfie: string | null
    work_status: string
    hr_status: string
    leave_type?: string | null
    marking_type: string
    worked_hours: string | null
    /** The 23:59 cron closed the day - hours are not real, so show NA. */
    is_auto_checkout?: boolean
    user_remarks: string | null
    approver_remarks: string | null
    approved_by: number | null
    approved_at: string | null
    /**
     * Second step for a day that had no attendance row at all and was created
     * through Manual Mark: the marker's own project manager has to confirm it.
     * 'not_required' on everything else, including every record from before the
     * second step existed.
     */
    verification_status?: string | null
    verification_required_from?: number | null
    verified_by?: number | null
    verified_at?: string | null
    verification_remarks?: string | null
    verification_pending_with?: {
        id: number
        first_name: string
        last_name: string
    } | null
    verifier?: {
        id: number
        first_name: string
        last_name: string
    } | null
    reporting_latitude: number | null
    reporting_longitude: number | null
    distance_from_reporting: number | null
    check_out_distance_from_reporting?: number | null
    created_at: string
    updated_at: string
    user?: AttendanceUser
    approver?: {
        id: number
        first_name: string
        last_name: string
    } | null
}

interface TeamMemberRow {
    userId: number
    user?: AttendanceUser
    reportingLatitude: string | number | null
    reportingLongitude: string | number | null
    statusesByDate: Record<string, WorkStatus | HRStatus>
    workStatusesByDate?: Record<string, WorkStatus>
    /** Leave type of each Leave Applied day (attendances.leave_type). */
    leaveTypesByDate?: Record<string, string>
}

interface CalendarDay {
    date: Date
    isCurrentMonth: boolean
    attendances: AttendanceRecord[]
}

interface AttendanceDateOffcanvasProps {
    isOpen: boolean
    onClose: () => void
    date: Date | null
    attendances: AttendanceRecord[]
    onApprove: (id: number) => void
    onManualMark: () => void
    onView: (attendance: AttendanceRecord) => void
    /** Read-only screens (HR > User Attendance) show the data and nothing else. */
    readOnly?: boolean
}

interface ApproveModalProps {
    isOpen: boolean
    onClose: () => void
    attendance: AttendanceRecord | null
    onSuccess: () => void
}

interface User {
    id: number
    first_name: string
    last_name: string
    email: string
}

interface ManualMarkModalProps {
    isOpen: boolean
    onClose: () => void
    selectedDate?: string
    /**
     * Employee to pre-select. Set when the modal is opened from a "Not Marked"
     * cell of the matrix, where the row already says who the day belongs to.
     */
    presetUserId?: number
    /**
     * Whether TODAY may be manually marked (Masters > Manual Attendance).
     * Future dates are never allowed, so this only moves the latest
     * selectable date between today and yesterday.
     */
    allowToday?: boolean
    onSuccess: () => void
}

interface CalculatePayableModalProps {
    isOpen: boolean
    onClose: () => void
    userId?: number
}

interface BulkApproveMonthModalProps {
    isOpen: boolean
    onClose: () => void
    currentDate: Date
    onSuccess: () => void
}

interface ViewDetailsOffcanvasProps {
    isOpen: boolean
    onClose: () => void
    attendance: AttendanceRecord | null
}

interface AttendanceFilters {
    search?: string
    work_status?: string
    hr_status?: string
    marking_type?: string
    user_id?: string
    user_name?: string
    project_id?: string
    project_name?: string
    department_id?: string
    department_name?: string
    role_id?: string
    role_name?: string
    association_type?: string
    attendance_date_from?: string
    attendance_date_to?: string
}

interface AttendanceSortConfig {
    field: string
    direction: "ASC" | "DESC"
}

enum WorkStatus {
    WORKED = "worked",
    LATE = "late",
    IDLE = "idle",
    ABSENT = "absent",
    LEAVE = "leave",
    WEEKLY_OFF = "weekly_off",
    HOLIDAY = "holiday",
    WORK_FROM_HOME = "work_from_home",
    PENDING = "pending",
    NOT_MARKED = "not_marked",
}

enum HRStatus {
    PRESENT = "present",
    ABSENT = "absent",
    PENDING = "pending",
    NOT_MARKED = "not_marked",
    // Display only — the server stores an approved Leave day as "absent".
    // See toHRMatrixStatus.
    LEAVE_APPLIED = "leave_applied",
}

const normalizeWorkStatus = (value: unknown): WorkStatus => {
    if (typeof value !== "string") return WorkStatus.PENDING
    const v = value.toLowerCase().trim()
    switch (v) {
        case WorkStatus.WORKED:
            return WorkStatus.WORKED
        case WorkStatus.LATE:
            return WorkStatus.LATE
        case WorkStatus.IDLE:
            return WorkStatus.IDLE
        case WorkStatus.ABSENT:
            return WorkStatus.ABSENT
        case WorkStatus.LEAVE:
            return WorkStatus.LEAVE
        case WorkStatus.WEEKLY_OFF:
            return WorkStatus.WEEKLY_OFF
        case WorkStatus.HOLIDAY:
            return WorkStatus.HOLIDAY
        case WorkStatus.WORK_FROM_HOME:
            return WorkStatus.WORK_FROM_HOME
        case WorkStatus.PENDING:
            return WorkStatus.PENDING
        case WorkStatus.NOT_MARKED:
            return WorkStatus.NOT_MARKED
        default:
            return WorkStatus.PENDING
    }
}

const getWorkStatusLabel = (status: WorkStatus) => {
    switch (status) {
        case WorkStatus.WORKED:
            return "Worked"
        case WorkStatus.LATE:
            return "Late"
        case WorkStatus.IDLE:
            return "Idle"
        case WorkStatus.ABSENT:
            return "Absent"
        case WorkStatus.LEAVE:
            return "Leave Applied"
        case WorkStatus.WEEKLY_OFF:
            return "Weekly Off"
        case WorkStatus.HOLIDAY:
            return "Holiday"
        case WorkStatus.WORK_FROM_HOME:
            return "Work From Home"
        case WorkStatus.PENDING:
            return "Pending Approval"
        case WorkStatus.NOT_MARKED:
            return "Not Marked"
        default:
            return "Pending Approval"
    }
}

const getWorkStatusAbbr = (status: WorkStatus) => {
    switch (status) {
        case WorkStatus.WORKED:
            return "W"
        case WorkStatus.LATE:
            return "LT"
        case WorkStatus.IDLE:
            return "I"
        case WorkStatus.ABSENT:
            return "A"
        case WorkStatus.LEAVE:
            return "LA"
        case WorkStatus.WEEKLY_OFF:
            return "OFF"
        case WorkStatus.HOLIDAY:
            return "HOL"
        case WorkStatus.WORK_FROM_HOME:
            return "WFH"
        case WorkStatus.PENDING:
            return "-"
        case WorkStatus.NOT_MARKED:
            return "NM"
        default:
            return "-"
    }
}

const getWorkStatusCellClasses = (status: WorkStatus) => {
    switch (status) {
        case WorkStatus.WORKED:
            return "bg-green-100 text-green-900"
        case WorkStatus.LATE:
            return "bg-amber-100 text-amber-900"
        case WorkStatus.IDLE:
            return "bg-yellow-100 text-yellow-900"
        case WorkStatus.ABSENT:
            return "bg-red-100 text-red-900"
        case WorkStatus.LEAVE:
            return "bg-blue-100 text-blue-900"
        case WorkStatus.WEEKLY_OFF:
            return "bg-gray-200 text-gray-800"
        case WorkStatus.HOLIDAY:
            return "bg-indigo-100 text-indigo-900"
        case WorkStatus.WORK_FROM_HOME:
            return "bg-teal-100 text-teal-900"
        case WorkStatus.PENDING:
            return "bg-gray-100 text-gray-700"
        case WorkStatus.NOT_MARKED:
            return "bg-slate-100 text-slate-700"
        default:
            return "bg-gray-100 text-gray-700"
    }
}

const normalizeHRStatus = (value: unknown): HRStatus => {
    if (typeof value !== "string") return HRStatus.PENDING
    switch (value.toLowerCase().trim()) {
        case HRStatus.PRESENT: return HRStatus.PRESENT
        case HRStatus.ABSENT: return HRStatus.ABSENT
        case HRStatus.NOT_MARKED: return HRStatus.NOT_MARKED
        case HRStatus.LEAVE_APPLIED: return HRStatus.LEAVE_APPLIED
        default: return HRStatus.PENDING
    }
}

/**
 * The HR-status matrix cell for one day. An approved Leave day is stored as HR
 * "absent" but reads "Leave Applied" here, so it is never shown or counted as
 * Absent. A Leave day still awaiting approval stays "Pending Approval" — only
 * approved leave counts as Leave Applied.
 */
const toHRMatrixStatus = (hrStatus: unknown, workStatus?: WorkStatus): HRStatus => {
    const status = normalizeHRStatus(hrStatus)
    return status === HRStatus.ABSENT && workStatus === WorkStatus.LEAVE ? HRStatus.LEAVE_APPLIED : status
}

// An Idle day is present for HR but is not a working day.
const WORKING_DAY_STATUSES = new Set<WorkStatus>([
    WorkStatus.WORKED,
    WorkStatus.LATE,
    WorkStatus.WORK_FROM_HOME,
])

/** Summary counts at the end of a work-status matrix row (screen and Excel). */
const countWorkMatrixRow = (statusesByDate: Record<string, string>, dates: string[]) => {
    const c = { worked: 0, late: 0, idle: 0, absent: 0, leave: 0, weekly_off: 0, holiday: 0, wfh: 0, pending: 0, not_marked: 0 }
    for (const ds of dates) {
        const s = statusesByDate[ds]
        if (s === undefined) { c.not_marked++; continue }
        if (s === WorkStatus.WORKED) c.worked++
        else if (s === WorkStatus.LATE) c.late++
        else if (s === WorkStatus.IDLE) c.idle++
        else if (s === WorkStatus.ABSENT) c.absent++
        else if (s === WorkStatus.LEAVE) c.leave++
        else if (s === WorkStatus.WEEKLY_OFF) c.weekly_off++
        else if (s === WorkStatus.HOLIDAY) c.holiday++
        else if (s === WorkStatus.WORK_FROM_HOME) c.wfh++
        else if (s === WorkStatus.NOT_MARKED) c.not_marked++
        else c.pending++
    }
    // A day still awaiting approval or verification arrives as "pending", so
    // these are settled days only. Idle days are not working days.
    // Present mirrors the server's approval rule (hr_status becomes present for
    // worked / late / idle / WFH / weekly off / holiday), same as the HR view.
    return {
        ...c,
        present: c.worked + c.late + c.idle + c.wfh + c.weekly_off + c.holiday,
        working_days: c.worked + c.late + c.wfh,
    }
}

/**
 * Summary counts at the end of an HR-status matrix row (screen and Excel).
 * Leave Applied is approved leave only (a pending one is in Pending Approval),
 * and Working Days counts a worked / late / WFH day only once it is approved
 * (HR Present) — a day still pending approval is not a working day, and
 * neither is an Idle one.
 */
const countHRMatrixRow = (
    statusesByDate: Record<string, string>,
    workStatusesByDate: Record<string, WorkStatus> | undefined,
    dates: string[],
    leaveTypesByDate?: Record<string, string>
) => {
    const c = { present: 0, absent: 0, pending: 0, not_marked: 0, leave_applied: 0, working_days: 0 }
    // Approved leave days per leave type. A leave day with no (or a retired)
    // type counts as Special Approval, as it does on the Leave Dashboard.
    const leave_by_type: Record<string, number> = Object.fromEntries(
        ATTENDANCE_LEAVE_TYPE_OPTIONS.map((option) => [option.value, 0])
    )
    for (const ds of dates) {
        const s = statusesByDate[ds]
        if (s === undefined) { c.not_marked++; continue }
        if (s === HRStatus.PRESENT) {
            c.present++
            const work = workStatusesByDate?.[ds]
            if (work && WORKING_DAY_STATUSES.has(work)) c.working_days++
        }
        else if (s === HRStatus.ABSENT) c.absent++
        else if (s === HRStatus.LEAVE_APPLIED) {
            c.leave_applied++
            const type = leaveTypesByDate?.[ds]
            leave_by_type[type && type in leave_by_type ? type : "other"]++
        }
        else if (s === HRStatus.NOT_MARKED) c.not_marked++
        else c.pending++
    }
    return { ...c, leave_by_type }
}

const getHRStatusLabel = (status: HRStatus) => {
    switch (status) {
        case HRStatus.PRESENT: return "Present"
        case HRStatus.ABSENT: return "Absent"
        case HRStatus.NOT_MARKED: return "Not Marked"
        case HRStatus.LEAVE_APPLIED: return "Leave Applied"
        default: return "Pending Approval"
    }
}

/** Matrix cell text; a Leave Applied day carries its leave type in brackets. */
const getMatrixCellLabel = (status: WorkStatus | HRStatus, isHRMode: boolean, leaveType?: string) => {
    const label = isHRMode ? getHRStatusLabel(status as HRStatus) : getWorkStatusLabel(status as WorkStatus)
    const isLeave = status === (isHRMode ? HRStatus.LEAVE_APPLIED : WorkStatus.LEAVE)
    const leaveTypeLabel = isLeave ? getAttendanceLeaveTypeLabel(leaveType) : ""
    return leaveTypeLabel ? `${label} (${leaveTypeLabel})` : label
}

const getHRStatusCellClasses = (status: HRStatus) => {
    switch (status) {
        case HRStatus.PRESENT: return "bg-green-100 text-green-900"
        case HRStatus.ABSENT: return "bg-red-100 text-red-900"
        case HRStatus.NOT_MARKED: return "bg-slate-100 text-slate-700"
        case HRStatus.LEAVE_APPLIED: return "bg-blue-100 text-blue-900"
        default: return "bg-gray-100 text-gray-700"
    }
}

const formatDateYMD = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
}

const parseYMDLocal = (ymd: string) => new Date(`${ymd}T00:00:00`)

const buildDateRange = (fromYMD: string, toYMD: string) => {
    const from = parseYMDLocal(fromYMD)
    const to = parseYMDLocal(toYMD)
    const dates: string[] = []
    const cur = new Date(from.getTime())
    while (cur.getTime() <= to.getTime()) {
        dates.push(formatDateYMD(cur))
        cur.setDate(cur.getDate() + 1)
    }
    return dates
}

const calculateAerialDistanceKm = (
    fromLat?: number | string | null,
    fromLng?: number | string | null,
    toLat?: number | string | null,
    toLng?: number | string | null
) => {
    // Number(null) and Number("") are 0, which would measure against (0, 0)
    // instead of reporting "no distance" — treat a missing value as missing.
    const values = [fromLat, fromLng, toLat, toLng]
    if (values.some((v) => v === null || v === undefined || v === "")) {
        return null
    }
    const startLat = Number(fromLat)
    const startLng = Number(fromLng)
    const endLat = Number(toLat)
    const endLng = Number(toLng)

    if (![startLat, startLng, endLat, endLng].every(Number.isFinite)) {
        return null
    }

    const toRad = (value: number) => (value * Math.PI) / 180
    const earthRadiusKm = 6371
    const dLat = toRad(endLat - startLat)
    const dLng = toRad(endLng - startLng)
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(startLat)) * Math.cos(toRad(endLat)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

    return earthRadiusKm * c
}

const formatName = (first?: string | null, last?: string | null) =>
    `${first ?? ""} ${last ?? ""}`.trim()

const escapeHtml = (value: unknown) =>
    String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")

/**
 * Has a manager already acted on this day?
 *
 * `approved_by` is stamped on approval and never cleared, so on its own it
 * would also mark a day the employee has since re-submitted. A day back in
 * "pending" is genuinely waiting on someone again, so it does not count as
 * approved and keeps its approve action.
 */
/**
 * A manual mark that is still waiting on the marker's project manager, or one
 * that manager turned down. Anything else (including every record that predates
 * the second step) renders nothing.
 */
const VerificationBadge: React.FC<{ record?: AttendanceRecord | null; className?: string }> = ({
    record,
    className = "",
}) => {
    const status = record?.verification_status
    if (status !== "pending" && status !== "rejected") return null

    const isPending = status === "pending"
    const waitingOn = record?.verification_pending_with
    const decidedBy = record?.verifier
    const title = isPending
        ? `Awaiting verification${waitingOn ? ` by ${formatName(waitingOn.first_name, waitingOn.last_name)}` : ""}`
        : `Verification rejected${decidedBy ? ` by ${formatName(decidedBy.first_name, decidedBy.last_name)}` : ""}${record?.verification_remarks ? ` — ${record.verification_remarks}` : ""}`

    return (
        <span
            title={title}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isPending ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"
                } ${className}`}
        >
            {isPending
                ? "Awaiting Verification"
                : (record as any)?.verification_type === "attendance_change_request"
                    ? "Change Rejected"
                    : "Verification Rejected"}
        </span>
    )
}

const isAttendanceApproved = (record?: AttendanceRecord | null): boolean =>
    Boolean(record?.approved_by) && record?.work_status !== WorkStatus.PENDING

const getAttendanceApproverName = (record?: AttendanceRecord | null) =>
    record?.approver
        ? formatName(record.approver.first_name, record.approver.last_name) || "N/A"
        : "N/A"

const getUserDistrictName = (user?: AttendanceUser | null) => {
    const district = user?.district
    if (!district) return "N/A"
    if (typeof district === "string") return district || "N/A"
    return district.name || "N/A"
}

const getUserProjectNames = (user?: AttendanceUser | null) => {
    const projects = user?.user_projects ?? []
    const names = projects
        .map((up) => up.project?.project_name)
        .filter((v): v is string => Boolean(v && v.trim()))
    return names.length ? names.join(", ") : "N/A"
}

// Inline "View More" cell for project names
const ProjectNamesCell: React.FC<{ user?: AttendanceUser | null }> = ({ user }) => {
    const projects = user?.user_projects ?? []
    const names = projects
        .map((up) => up.project?.project_name)
        .filter((v): v is string => Boolean(v && v.trim()))
    const [expanded, setExpanded] = useState(false)
    const SHOW_COUNT = 2
    if (!names.length) return <span className="text-gray-400">N/A</span>
    if (names.length <= SHOW_COUNT) return <span>{names.join(', ')}</span>
    return (
        <div className="relative">
            <span>{names.slice(0, SHOW_COUNT).join(', ')}</span>
            {!expanded ? (
                <button
                    onClick={() => setExpanded(true)}
                    className="ml-1 text-blue-700 text-xs font-medium hover:underline"
                >
                    +{names.length - SHOW_COUNT} more
                </button>
            ) : (
                <div className="absolute z-20 bg-white border border-gray-200 rounded shadow-lg p-3 left-0 top-6 w-60 max-h-48 overflow-y-auto">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-semibold text-gray-700">All Projects</span>
                        <button onClick={() => setExpanded(false)} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>
                    </div>
                    <ul className="space-y-1">
                        {names.map((n, i) => (
                            <li key={i} className="text-xs text-gray-800 py-0.5 border-b border-gray-100 last:border-0">{n}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    )
}

const getUserProjectGroupNames = (user?: AttendanceUser | null) => {
    const projects = user?.user_projects ?? []
    const names = projects
        .map((up) => up.project?.client_project?.client_project_name)
        .filter((v): v is string => Boolean(v && v.trim()))

    return Array.from(new Set(names))
}

const getUserDepartmentOrProjectGroup = (user?: AttendanceUser | null) => {
    const projectGroups = getUserProjectGroupNames(user)
    if (projectGroups.length > 0) {
        return projectGroups.join(", ")
    }
    return user?.department?.name?.trim() || 'N/A'
}

interface FilterModalProps {
    isOpen: boolean
    onClose: () => void
    filters: AttendanceFilters
    onFiltersChange: (filters: AttendanceFilters) => void
    onApply: () => void
    onReset: () => void
    memberOnly?: boolean
    activeTab: "direct" | "project" | "shared_projects"
    projectOptions: SelectOption[]
    /** Department groups (client projects), loaded by the page so the dropdown
     *  lists them the moment it opens instead of only after a 3-character search. */
    departmentGroupOptions: SelectOption[]
    /** Roles, for the role-wise filter. */
    roleOptions: SelectOption[]
}

// Filter Modal Component
const FilterModal: React.FC<FilterModalProps> = ({
    isOpen,
    onClose,
    filters,
    onFiltersChange,
    onApply,
    onReset,
    memberOnly = false,
    activeTab,
    projectOptions,
    departmentGroupOptions,
    roleOptions,
}) => {
    if (!isOpen) return null

    // Must be the same population the page itself lists. The shared-projects
    // screen (HR > User Attendance) used to search the reporting hierarchy
    // instead, so colleagues on the same project who do not report to the
    // viewer could never be picked.
    const employeeSearchEndpoint = activeTab === "direct"
        ? "/attendance/team-members/direct"
        : activeTab === "shared_projects"
            ? "/attendance/team-members/shared-projects"
            : "/attendance/team-members/all"

    const handleInputChange = (field: keyof AttendanceFilters, value: string) => {
        onFiltersChange({
            ...filters,
            [field]: value || undefined
        })
    }

    const handleUserSelect = (option: SelectOption | null) => {
        onFiltersChange({
            ...filters,
            user_id: option ? String(option.value) : undefined,
            user_name: option ? option.label : undefined,
        })
    }

    const handleProjectSelect = (option: SelectOption | null) => {
        onFiltersChange({
            ...filters,
            project_id: option ? String(option.value) : undefined,
            project_name: option ? option.label : undefined,
        })
    }

    const handleDepartmentGroupSelect = (option: SelectOption | null) => {
        onFiltersChange({
            ...filters,
            department_id: option ? String(option.value) : undefined,
            department_name: option ? option.label : undefined,
        })
    }

    const handleRoleSelect = (option: SelectOption | null) => {
        onFiltersChange({
            ...filters,
            role_id: option ? String(option.value) : undefined,
            role_name: option ? option.label : undefined,
        })
    }


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
            <div className="bg-white rounded shadow-2xl max-w-2xl w-full max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg sm:text-xl font-semibold text-white flex items-center gap-2">
                            <FiFilter className="w-5 h-5" />
                            Filters
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-white hover:text-gray-200 transition-colors"
                        >
                            <FiX className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 overflow-y-auto overscroll-contain flex-1 min-h-0">
                    <div className={`grid grid-cols-1 ${memberOnly ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-4 sm:gap-6`}>
                        {!memberOnly && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Work Status</label>
                                    <select
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                                        value={filters.work_status || ''}
                                        onChange={(e) => handleInputChange('work_status', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="worked">Worked</option>
                                        <option value="late">Late</option>
                                        <option value="idle">Idle</option>
                                        <option value="absent">Absent</option>
                                        <option value="leave">Leave Applied</option>
                                        <option value="weekly_off">Weekly Off</option>
                                        <option value="holiday">Holiday</option>
                                        <option value="work_from_home">Work From Home</option>
                                        <option value="pending">Pending Approval</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">HR Status</label>
                                    <select
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                                        value={filters.hr_status || ''}
                                        onChange={(e) => handleInputChange('hr_status', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="present">Present</option>
                                        <option value="absent">Absent</option>
                                        <option value="pending">Pending Approval</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Association</label>
                                    <select
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                                        value={filters.association_type || ''}
                                        onChange={(e) => handleInputChange('association_type', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="inhouse">Onrole</option>
                                        <option value="vendor">Offrole</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Marking Type</label>
                                    <select
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                                        value={filters.marking_type || ''}
                                        onChange={(e) => handleInputChange('marking_type', e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="auto">Auto</option>
                                        <option value="manual">Manual</option>
                                    </select>
                                </div>
                            </>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
                            {/* Lists employees as soon as it opens (no 3-character wait)
                                and pages through them as you scroll; typing narrows the
                                list server-side. */}
                            <SelectWithSearch
                                key={employeeSearchEndpoint}
                                placeholder="Select employee"
                                value={
                                    filters.user_id
                                        ? {
                                            id: Number(filters.user_id),
                                            value: Number(filters.user_id),
                                            label: filters.user_name || "Selected employee",
                                        }
                                        : null
                                }
                                onChange={handleUserSelect}
                                apiEndpoint={employeeSearchEndpoint}
                                dataKey="members"
                                valueKey="userId"
                                minSearchLength={0}
                                portalMenu
                                displayFormat={(item: any) => {
                                    const name = [item.user?.first_name, item.user?.last_name].filter(Boolean).join(" ") || "Unnamed Employee"
                                    return item.user?.employee_code ? `${name} (${item.user.employee_code})` : name
                                }}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
                            <SearchableSelect
                                value={
                                    filters.project_id
                                        ? {
                                            value: Number(filters.project_id),
                                            label: filters.project_name || "Selected project",
                                        }
                                        : null
                                }
                                onChange={handleProjectSelect}
                                options={projectOptions}
                                placeholder="Select project"
                                portalMenu
                                minSearchLength={3}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Department Group</label>
                            {/* Was a search-as-you-type box against /client-projects/list, which
                                fetches nothing until three characters are typed — so opening the
                                dropdown showed an empty list and the groups looked missing. The
                                page loads them once and they are listed here outright. */}
                            <SearchableSelect
                                value={
                                    filters.department_id
                                        ? {
                                            value: Number(filters.department_id),
                                            label: filters.department_name || "Selected department group",
                                        }
                                        : null
                                }
                                onChange={handleDepartmentGroupSelect}
                                options={departmentGroupOptions}
                                placeholder="Select department group"
                                portalMenu
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                            <SearchableSelect
                                value={
                                    filters.role_id
                                        ? {
                                            value: Number(filters.role_id),
                                            label: filters.role_name || "Selected role",
                                        }
                                        : null
                                }
                                onChange={handleRoleSelect}
                                options={roleOptions}
                                placeholder="Select role"
                                portalMenu
                            />
                        </div>
                        {!memberOnly && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date From</label>
                                    <input
                                        type="date"
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                                        value={filters.attendance_date_from || ''}
                                        onChange={(e) => handleInputChange('attendance_date_from', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Date To</label>
                                    <input
                                        type="date"
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors"
                                        value={filters.attendance_date_to || ''}
                                        onChange={(e) => handleInputChange('attendance_date_to', e.target.value)}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap justify-between items-center gap-2">
                    <button
                        onClick={onReset}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
                    >
                        <FiRefreshCw className="w-4 h-4" />
                        Reset Filters
                    </button>

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                onApply()
                                onClose()
                            }}
                            className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-800 transition-colors flex items-center gap-2"
                        >
                            <FiFilter className="w-4 h-4" />
                            Apply Filters
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

// Approve Attendance Modal - UPDATED WITH ALL DETAILS AND CIRCULAR SELFIES
const ApproveModal: React.FC<ApproveModalProps> = ({ isOpen, onClose, attendance, onSuccess }) => {
    const [formData, setFormData] = useState({
        work_status: "worked",
        approver_remarks: "",
        leave_type: DEFAULT_ATTENDANCE_LEAVE_TYPE,
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [quotaBlocked, setQuotaBlocked] = useState(false)

    useEffect(() => {
        if (attendance) {
            setFormData({
                leave_type: attendance.leave_type || DEFAULT_ATTENDANCE_LEAVE_TYPE,
                // Reflect the record's actual work status, but never submit
                // "pending" as an approval decision — the backend treats
                // "pending" as not-present, so approving a freshly-marked
                // (pending) record would leave it "Pending Approval" / HR
                // "Absent". Default such records to "worked" for the approval
                // form only. This default is local to the form: the modal's
                // "Current Status"/"HR Status" and every table read from
                // `attendance.*`, so the view still shows "Pending Approval".
                work_status:
                    attendance.work_status === "pending"
                        ? "worked"
                        : attendance.work_status,
                approver_remarks: attendance.approver_remarks || "",
            })
        }
    }, [attendance])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!attendance) return

        try {
            setLoading(true)
            setError("")

            // "pending" is not a valid approval decision — the backend treats
            // it as not-present (HR Absent) and leaves the record "Pending
            // Approval". If the Work Status select never fired onChange (its
            // value fell outside the option list), formData could still hold
            // "pending"; coerce it to "worked" so approving always resolves the
            // record to a present state.
            const workStatus =
                !formData.work_status || formData.work_status === "pending"
                    ? "worked"
                    : formData.work_status

            const { leave_type, ...approveFields } = formData
            await axiosInstance.post("/attendance/approve", {
                id: attendance.id,
                ...approveFields,
                work_status: workStatus,
                // Only a Leave Applied day carries a leave type.
                ...(workStatus === "leave" ? { leave_type } : {}),
            })

            onSuccess()
            onClose()
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to approve attendance")
        } finally {
            setLoading(false)
        }
    }

    if (!isOpen || !attendance) return null

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-6">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

                <div className="relative bg-white rounded shadow-xl max-w-5xl w-full p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">Approve Attendance</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                            {/* Employee Details : mb-3 p-3 bg-blue-50 rounded border border-blue-200*/}
                            <div className="p-3 bg-blue-50 rounded border border-blue-200">
                                <div className="flex items-center gap-3 mb-1.5">
                                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                                        <FiUser className="w-6 h-6 text-blue-800" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900">
                                            {attendance.user?.first_name} {attendance.user?.last_name}
                                        </div>
                                        <div className="text-sm text-gray-600">{attendance.user?.email}</div>
                                        {attendance.user?.association_type && (
                                            <div className="text-xs text-gray-500 capitalize">{attendance.user.association_type}</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Selfies Section - CIRCULAR */}
                            {(attendance.check_in_selfie || attendance.check_out_selfie) && (
                                <div className="p-3 bg-gray-50 rounded">
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Selfies</h4>
                                    <div className="flex gap-4 justify-center">
                                        {attendance.check_in_selfie && (
                                            <div className="flex flex-col items-center">
                                                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-green-500 shadow-lg mb-1.5 cursor-pointer">
                                                    <img
                                                        src={attendance.check_in_selfie || "/placeholder.svg"}
                                                        alt="Check In Selfie"
                                                        className="w-full h-full object-cover transition-transform duration-200 ease-out hover:scale-150"
                                                        onClick={() => window.open(attendance.check_in_selfie as string, "_blank")}
                                                    />
                                                </div>
                                                <span className="text-xs font-medium text-green-600">Check In</span>
                                            </div>
                                        )}
                                        {attendance.check_out_selfie && (
                                            <div className="flex flex-col items-center">
                                                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-red-500 shadow-lg mb-1.5 cursor-pointer">
                                                    <img
                                                        src={attendance.check_out_selfie || "/placeholder.svg"}
                                                        alt="Check Out Selfie"
                                                        className="w-full h-full object-cover transition-transform duration-200 ease-out hover:scale-150"
                                                        onClick={() => window.open(attendance.check_out_selfie as string, "_blank")}
                                                    />
                                                </div>
                                                <span className="text-xs font-medium text-red-600">Check Out</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Attendance Details - ALL FIELDS */}
                            <div className="p-3 bg-gray-50 rounded">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Attendance Details</h4>
                                <div className="grid grid-cols-2 gap-3 text-xs md:text-sm">
                                    <div>
                                        <span className="text-gray-600">Date:</span>
                                        <div className="font-medium text-gray-900">
                                            {new Date(attendance.attendance_date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Marking Type:</span>
                                        <div className="font-medium mt-0.5">
                                            <span
                                                className={`px-2 py-0.5 rounded text-xs ${attendance.marking_type === "auto" ? "bg-blue-100 text-blue-900" : "bg-orange-100 text-orange-700"}`}
                                            >
                                                {attendance.marking_type}
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Check In Time:</span>
                                        <div className="font-medium text-gray-900">
                                            {attendance.check_in_time ? new Date(attendance.check_in_time).toLocaleTimeString() : "N/A"}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Check Out Time:</span>
                                        <div className="font-medium text-gray-900">
                                            {attendance.check_out_time ? new Date(attendance.check_out_time).toLocaleTimeString() : "N/A"}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Current Status:</span>
                                        <div className="font-medium mt-0.5">
                                            <span
                                                className={`px-2 py-0.5 rounded-full text-xs ${attendance.work_status === "worked"
                                                    ? "bg-green-100 text-green-700"
                                                    : attendance.work_status === "pending"
                                                        ? "bg-gray-100 text-gray-700"
                                                        : "bg-red-100 text-red-700"}`}
                                            >
                                                {getWorkStatusLabel(normalizeWorkStatus(attendance.work_status))}
                                                {attendance.work_status === "leave" && attendance.leave_type
                                                    ? ` (${getAttendanceLeaveTypeLabel(attendance.leave_type)})`
                                                    : ""}
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">HR Status:</span>
                                        <div className="font-medium mt-0.5">
                                            <span
                                                className={`px-2 py-0.5 rounded-full text-xs ${getDisplayHRStatusBadgeClasses(attendance.hr_status, attendance.work_status)}`}
                                            >
                                                {getDisplayHRStatusLabel(attendance.hr_status, attendance.work_status)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {/* Location Details — always shown, missing values say so */}
                            <AttendanceLocationDetails record={attendance} />

                            {/* Remarks */}
                            {(attendance.user_remarks || attendance.approver_remarks) && (
                                <div className="p-3 bg-gray-50 rounded">
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">Remarks</h4>
                                    <div className="space-y-2 text-xs md:text-sm">
                                        {attendance.user_remarks && (
                                            <div>
                                                <span className="text-gray-600">User Remarks:</span>
                                                <div className="text-gray-900 mt-0.5 p-2 bg-white rounded border border-gray-200 max-h-20 overflow-y-auto text-xs md:text-sm">
                                                    {attendance.user_remarks}
                                                </div>
                                            </div>
                                        )}
                                        {attendance.approver_remarks && (
                                            <div>
                                                <span className="text-gray-600">Previous Approver Remarks:</span>
                                                <div className="text-gray-900 mt-0.5 p-2 bg-white rounded border border-gray-200 max-h-20 overflow-y-auto text-xs md:text-sm">
                                                    {attendance.approver_remarks}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Approval History */}
                            {attendance.approved_by && attendance.approver && (
                                <div className="p-3 bg-green-50 rounded border border-green-200">
                                    <h4 className="text-sm font-medium text-green-700 mb-1.5">Previous Approval</h4>
                                    <div className="text-xs md:text-sm text-gray-700">
                                        Approved by {attendance.approver.first_name} {attendance.approver.last_name} on{" "}
                                        {attendance.approved_at ? new Date(attendance.approved_at).toLocaleString() : "N/A"}
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className="p-2.5 bg-red-50 border border-red-200 rounded">
                                    <p className="text-xs md:text-sm text-red-700">{error}</p>
                                </div>
                            )}

                            {/* Approval Form */}
                            <form onSubmit={handleSubmit} className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Work Status *</label>
                                    <select
                                        value={formData.work_status}
                                        onChange={(e) => setFormData({ ...formData, work_status: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                        required
                                    >
                                        {/* <option value="pending">Pending</option> */}
                                        {/* The HR status in brackets is display only: it is what the
                                            server derives from the work status on approval
                                            (approveAttendance), so the approver can see the payroll
                                            effect before submitting. Option values are unchanged.
                                            Leave Applied is stored as HR "absent" but every
                                            Attendance view shows it as Leave Applied. */}
                                        <option value="worked">Worked (Present)</option>
                                        <option value="late">Late (Present)</option>
                                        <option value="work_from_home">Work From Home (Present)</option>
                                        <option value="idle">Idle (Present)</option>
                                        <option value="absent">Absent (Absent)</option>
                                        <option value="leave">Leave Applied</option>
                                        <option value="weekly_off">Weekly Off (Present)</option>
                                        <option value="holiday">Holiday (Present)</option>
                                    </select>
                                </div>

                                {formData.work_status === "leave" && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type *</label>
                                        <select
                                            value={formData.leave_type}
                                            onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                            required
                                        >
                                            {ATTENDANCE_LEAVE_TYPE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                        <LeaveQuotaHint
                                            userId={attendance.user_id}
                                            date={attendance.attendance_date}
                                            leaveType={formData.leave_type}
                                            onBlockedChange={setQuotaBlocked}
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                                    <textarea
                                        value={formData.approver_remarks}
                                        onChange={(e) => setFormData({ ...formData, approver_remarks: e.target.value })}
                                        rows={2}
                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                        placeholder="Add your remarks..."
                                    />
                                </div>

                                <div className="flex gap-3 pt-2">
                                    <button
                                        type="submit"
                                        disabled={loading || (formData.work_status === "leave" && quotaBlocked)}
                                        className="flex-1 bg-blue-800 text-white px-4 py-2 rounded hover:bg-blue-900 transition-colors text-sm font-medium disabled:opacity-50"
                                    >
                                        {loading ? "Approving..." : "Approve"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

const BulkApproveMonthModal: React.FC<BulkApproveMonthModalProps> = ({ isOpen, onClose, currentDate, onSuccess }) => {
    const [users, setUsers] = useState<User[]>([])
    const [selectedUser, setSelectedUser] = useState<{ value: string | number; label: string } | null>(null)
    const [formData, setFormData] = useState({ work_status: "worked", approver_remarks: "", only_pending: true })
    const [loading, setLoading] = useState(false)
    const [usersLoading, setUsersLoading] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        if (!isOpen) return
        setError("")
        setUsersLoading(true)
        axiosInstance.get("/users/list?src=attendance", { params: { page: 1, limit: 100 } })
            .then((response) => setUsers(response.data?.data?.users || []))
            .catch(() => setError("Failed to load employees"))
            .finally(() => setUsersLoading(false))
    }, [isOpen])

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        if (!selectedUser) { setError("Please select an employee"); return }
        try {
            setLoading(true)
            setError("")
            await axiosInstance.post("/attendance/approve-month", {
                user_id: Number(selectedUser.value),
                month: currentDate.getMonth() + 1,
                year: currentDate.getFullYear(),
                ...formData,
            })
            onSuccess()
            onClose()
            setSelectedUser(null)
            setFormData({ work_status: "worked", approver_remarks: "", only_pending: true })
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to approve monthly attendance")
        } finally { setLoading(false) }
    }

    if (!isOpen) return null
    const userOptions = users.map((user) => ({ value: user.id, label: `${user.first_name} ${user.last_name} (${user.email})` }))
    const monthLabel = currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center px-4 py-6">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
                <div className="relative w-full max-w-lg rounded bg-white p-6 shadow-xl">
                    <div className="mb-4 flex items-center justify-between"><div><h3 className="text-lg font-semibold text-gray-900">Bulk Approve Attendance</h3><p className="text-sm text-gray-500">Apply a status to existing attendance records for {monthLabel}.</p></div><button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX className="h-5 w-5" /></button></div>
                    {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div><label className="mb-1 block text-sm font-medium text-gray-700">Employee *</label><SearchableSelect value={selectedUser} onChange={setSelectedUser} options={userOptions} placeholder="Select an employee" isLoading={usersLoading} emptyMessage="No employees found" minSearchLength={3} /></div>
                        <div><label className="mb-1 block text-sm font-medium text-gray-700">Work Status *</label><select value={formData.work_status} onChange={(e) => setFormData({ ...formData, work_status: e.target.value })} className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"><option value="worked">Worked (Present)</option><option value="late">Late (Present)</option><option value="work_from_home">Work From Home (Present)</option><option value="idle">Idle (Present)</option><option value="absent">Absent (Absent)</option><option value="leave">Leave Applied</option><option value="weekly_off">Weekly Off (Present)</option><option value="holiday">Holiday (Present)</option></select></div>
                        <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={formData.only_pending} onChange={(e) => setFormData({ ...formData, only_pending: e.target.checked })} /> Update pending records only</label>
                        <div><label className="mb-1 block text-sm font-medium text-gray-700">Remarks</label><textarea value={formData.approver_remarks} onChange={(e) => setFormData({ ...formData, approver_remarks: e.target.value })} rows={2} className="w-full resize-none rounded border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" /></div>
                        <div className="flex gap-3 pt-2"><button type="submit" disabled={loading} className="flex-1 rounded bg-blue-800 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900 disabled:opacity-50">{loading ? "Approving..." : "Approve Month"}</button><button type="button" onClick={onClose} className="flex-1 rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button></div>
                    </form>
                </div>
            </div>
        </div>
    )
}

// Manual Mark Attendance Modal
const ManualMarkModal: React.FC<ManualMarkModalProps> = ({ isOpen, onClose, selectedDate, presetUserId, allowToday = true, onSuccess }) => {
    // The latest day that can be marked: today, or yesterday while marking
    // today is switched off. A date that arrives later than that (a future day
    // picked on the calendar) is pulled back to it rather than left to fail.
    const maxDate = getManualMarkMaxDate(allowToday)
    const clampDate = (date?: string) => (!date || date > maxDate ? maxDate : date)
    const [quotaBlocked, setQuotaBlocked] = useState(false)

    const [formData, setFormData] = useState({
        user_id: "",
        attendance_date: clampDate(selectedDate),
        check_in_time: "",
        check_out_time: "",
        work_status: "worked",
        approver_remarks: "",
        leave_type: DEFAULT_ATTENDANCE_LEAVE_TYPE,
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [users, setUsers] = useState<User[]>([])
    const [usersLoading, setUsersLoading] = useState(false)
    const [selectedUser, setSelectedUser] = useState<{ value: string | number; label: string } | null>(null)

    useEffect(() => {
        if (!isOpen) return
        setFormData((prev) => ({ ...prev, attendance_date: clampDate(selectedDate || prev.attendance_date) }))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, selectedDate, maxDate])

    useEffect(() => {
        if (isOpen) {
            fetchUsers()
        }
    }, [isOpen])

    // Pre-select the employee when the modal was opened from a matrix cell.
    // The id is applied straight away so the form is submittable even before
    // the user list arrives; the visible label fills in once it does.
    useEffect(() => {
        if (!isOpen || !presetUserId) return
        setFormData((prev) => ({ ...prev, user_id: String(presetUserId) }))
    }, [isOpen, presetUserId])

    useEffect(() => {
        if (!isOpen || !presetUserId) return
        const match = users.find((user) => Number(user.id) === Number(presetUserId))
        if (!match) return
        setSelectedUser({
            value: match.id,
            label: `${match.first_name} ${match.last_name} (${match.email})`,
        })
    }, [isOpen, presetUserId, users])

    const fetchUsers = async () => {
        setUsersLoading(true)
        try {
            const response = await axiosInstance.get("/users/list?src=attendance", {
                params: {
                    page: 1,
                    limit: 100,
                },
            })
            if (response.data.success && response.data.data.users) {
                setUsers(response.data.data.users)
            }
        } catch (err) {
            console.error("Failed to fetch users:", err)
        } finally {
            setUsersLoading(false)
        }
    }

    const handleUserSelect = (option: { value: string | number; label: string } | null) => {
        setSelectedUser(option)
        setFormData({ ...formData, user_id: option ? String(option.value) : "" })
    }

    const userOptions = users.map((user) => ({
        value: user.id,
        label: `${user.first_name} ${user.last_name} (${user.email})`,
    }))

    const formatManualTimestamp = (date: string, time: string) => {
        if (!date || !time) return ""

        const [year, month, day] = date.split("-").map((part) => Number(part))
        const [rawHours = "00", rawMinutes = "00", rawSeconds = "00"] = time.split(":")
        const [secondsPart = "00", millisecondsPart = "000"] = rawSeconds.split(".")

        const hours = Number(rawHours)
        const minutes = Number(rawMinutes)
        const seconds = Number(secondsPart)
        const milliseconds = Number(millisecondsPart.padEnd(3, "0").slice(0, 3))

        // Treat input date/time as IST and convert to UTC string with +00 suffix.
        const IST_OFFSET_MINUTES = 330
        const utcMillis = Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds) - (IST_OFFSET_MINUTES * 60 * 1000)
        const utcDate = new Date(utcMillis)

        const utcYear = utcDate.getUTCFullYear()
        const utcMonth = String(utcDate.getUTCMonth() + 1).padStart(2, "0")
        const utcDay = String(utcDate.getUTCDate()).padStart(2, "0")
        const utcHours = String(utcDate.getUTCHours()).padStart(2, "0")
        const utcMinutes = String(utcDate.getUTCMinutes()).padStart(2, "0")
        const utcSeconds = String(utcDate.getUTCSeconds()).padStart(2, "0")
        const utcMilliseconds = String(utcDate.getUTCMilliseconds()).padStart(3, "0")

        return `${utcYear}-${utcMonth}-${utcDay} ${utcHours}:${utcMinutes}:${utcSeconds}.${utcMilliseconds}+00`
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.user_id) {
            setError("Please select an employee")
            return
        }

        // The date input's `max` stops the picker, not a typed value.
        const dateError = getManualMarkDateError(formData.attendance_date, allowToday)
        if (dateError) {
            setError(dateError)
            return
        }

        try {
            setLoading(true)
            setError("")

            const payload: any = {
                user_id: Number.parseInt(formData.user_id),
                attendance_date: formData.attendance_date,
                work_status: formData.work_status,
                approver_remarks: formData.approver_remarks,
            }
            // A Leave Applied day carries its leave type (Casual unless changed);
            // the verifier can still change it on the Verifications table.
            if (formData.work_status === "leave") {
                payload.leave_type = formData.leave_type || DEFAULT_ATTENDANCE_LEAVE_TYPE
            }

            if (formData.check_in_time) {
                payload.check_in_time = formatManualTimestamp(formData.attendance_date, formData.check_in_time)
            }
            if (formData.check_out_time) {
                payload.check_out_time = formatManualTimestamp(formData.attendance_date, formData.check_out_time)
            }

            await axiosInstance.post("/attendance/manual-mark", payload)

            onSuccess()
            onClose()
            setFormData({
                user_id: "",
                attendance_date: maxDate,
                check_in_time: "",
                check_out_time: "",
                work_status: "worked",
                approver_remarks: "",
                leave_type: DEFAULT_ATTENDANCE_LEAVE_TYPE,
            })
            setSelectedUser(null)
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to mark attendance")
        } finally {
            setLoading(false)
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

                <div className="relative bg-white rounded shadow-xl max-w-lg w-full p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Manual Mark Attendance</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
                            <SearchableSelect
                                value={selectedUser}
                                onChange={handleUserSelect}
                                options={userOptions}
                                placeholder="Select an employee"
                                isLoading={usersLoading}
                                emptyMessage="No employees found"
                                minSearchLength={3}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Attendance Date *</label>
                            <input
                                type="date"
                                value={formData.attendance_date}
                                max={maxDate}
                                onChange={(e) => setFormData({ ...formData, attendance_date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                required
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                {allowToday
                                    ? "Today or an earlier date. Future dates cannot be marked."
                                    : "Earlier dates only — manual attendance for today is currently disabled."}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Check In Time</label>
                                <input
                                    type="time"
                                    value={formData.check_in_time}
                                    onChange={(e) => setFormData({ ...formData, check_in_time: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Check Out Time</label>
                                <input
                                    type="time"
                                    value={formData.check_out_time}
                                    onChange={(e) => setFormData({ ...formData, check_out_time: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Work Status *</label>
                            <select
                                value={formData.work_status}
                                onChange={(e) => setFormData({ ...formData, work_status: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                required
                            >
                                {/* Same bracketed HR status as the approve modal:
                                    what the day becomes once it is approved. */}
                                <option value="worked">Worked (Present)</option>
                                <option value="late">Late (Present)</option>
                                <option value="work_from_home">Work From Home (Present)</option>
                                <option value="idle">Idle (Present)</option>
                                <option value="absent">Absent (Absent)</option>
                                <option value="leave">Leave Applied</option>
                                <option value="weekly_off">Weekly Off (Present)</option>
                                <option value="holiday">Holiday (Present)</option>
                                <option value="pending">Pending Approval</option>
                            </select>
                        </div>

                        {formData.work_status === "leave" && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type *</label>
                                <select
                                    value={formData.leave_type}
                                    onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                    required
                                >
                                    {ATTENDANCE_LEAVE_TYPE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                <LeaveQuotaHint
                                    userId={formData.user_id}
                                    date={formData.attendance_date}
                                    leaveType={formData.leave_type}
                                    onBlockedChange={setQuotaBlocked}
                                />
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                            <textarea
                                value={formData.approver_remarks}
                                onChange={(e) => setFormData({ ...formData, approver_remarks: e.target.value })}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                placeholder="Add remarks..."
                            />
                        </div>

                        <div className="flex gap-3 pt-4">
                            <button
                                type="submit"
                                disabled={loading || (formData.work_status === "leave" && quotaBlocked)}
                                className="flex-1 bg-blue-800 text-white px-4 py-2 rounded hover:bg-blue-900 transition-colors text-sm font-medium disabled:opacity-50"
                            >
                                {loading ? "Marking..." : "Mark Attendance"}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

// Calculate Payable Modal
const CalculatePayableModal: React.FC<CalculatePayableModalProps> = ({ isOpen, onClose, userId }) => {
    const [formData, setFormData] = useState({
        user_id: userId?.toString() || "",
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        salary: "",
    })
    const [result, setResult] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        if (userId) {
            setFormData((prev) => ({ ...prev, user_id: userId.toString() }))
        }
    }, [userId])

    const calculatePayable = async (e: React.FormEvent) => {
        e.preventDefault()

        try {
            setLoading(true)
            setError("")

            const response = await axiosInstance.post("/attendance/calculate-payable", {
                user_id: Number.parseInt(formData.user_id),
                month: formData.month,
                year: formData.year,
            })

            setResult(response.data.data)
        } catch (err: any) {
            setError(err.response?.data?.message || "Failed to calculate")
        } finally {
            setLoading(false)
        }
    }

    const calculateAmount = () => {
        if (!result || !formData.salary) return null

        const monthlySalary = Number.parseFloat(formData.salary)
        const perDaySalary = monthlySalary / result.total_days_in_month
        const totalAmount = perDaySalary * result.payable_days

        return {
            monthlySalary,
            perDaySalary,
            totalAmount,
            deduction: monthlySalary - totalAmount,
        }
    }

    const amounts = calculateAmount()

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
                <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

                <div className="relative bg-white rounded shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Calculate Payable Attendance</h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Form */}
                        <div>
                            <form onSubmit={calculatePayable} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                                        <input
                                            type="number"
                                            value={formData.year}
                                            onChange={(e) => setFormData({ ...formData, year: Number.parseInt(e.target.value) })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                            min="2020"
                                            max="2100"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Salary</label>
                                    <input
                                        type="number"
                                        value={formData.salary}
                                        onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                        placeholder="Enter salary to calculate amount"
                                        step="0.01"
                                    />
                                </div>

                                {error && (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded">
                                        <p className="text-sm text-red-700">{error}</p>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-blue-800 text-white px-4 py-2 rounded hover:bg-blue-900 transition-colors text-sm font-medium disabled:opacity-50"
                                >
                                    {loading ? "Calculating..." : "Calculate"}
                                </button>
                            </form>
                        </div>

                        {/* Results */}
                        <div>
                            {result ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="p-3 bg-gray-50 rounded">
                                            <div className="text-xs text-gray-600 mb-1">Total Days</div>
                                            <div className="text-xl font-bold text-gray-900">{result.total_days_in_month}</div>
                                        </div>
                                        <div className="p-3 bg-green-50 rounded">
                                            <div className="text-xs text-green-600 mb-1">Present</div>
                                            <div className="text-xl font-bold text-green-600">{result.present_days}</div>
                                        </div>
                                        <div className="p-3 bg-blue-50 rounded">
                                            <div className="text-xs text-blue-800 mb-1">Leave Applied</div>
                                            <div className="text-xl font-bold text-blue-800">{result.approved_leave_days}</div>
                                        </div>
                                        <div className="p-3 bg-purple-50 rounded">
                                            <div className="text-xs text-purple-600 mb-1">Holidays</div>
                                            <div className="text-xl font-bold text-purple-600">{result.holidays_count}</div>
                                        </div>
                                        <div className="p-3 bg-red-50 rounded">
                                            <div className="text-xs text-red-600 mb-1">Sundays</div>
                                            <div className="text-xl font-bold text-red-600">{result.sundays_count}</div>
                                        </div>
                                        <div className="p-3 bg-indigo-50 rounded border-2 border-indigo-300">
                                            <div className="text-xs text-indigo-600 mb-1 font-medium">Payable</div>
                                            <div className="text-xl font-bold text-indigo-600">{result.payable_days}</div>
                                        </div>
                                    </div>

                                    {amounts && (
                                        <div className="p-4 bg-green-50 rounded border border-green-200">
                                            <div className="space-y-2 text-sm">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Monthly Salary:</span>
                                                    <span className="font-medium">₹{amounts.monthlySalary.toLocaleString("en-IN")}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-600">Per Day:</span>
                                                    <span className="font-medium">₹{amounts.perDaySalary.toFixed(2)}</span>
                                                </div>
                                                <div className="flex justify-between pt-2 border-t border-green-300">
                                                    <span className="font-semibold text-green-900">Total Payable:</span>
                                                    <span className="font-bold text-green-600">
                                                        ₹{amounts.totalAmount.toLocaleString("en-IN")}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="h-full flex items-center justify-center">
                                    <div className="text-center text-gray-400">
                                        <FaCalculator className="w-12 h-12 mx-auto mb-2" />
                                        <p className="text-sm">Calculate to see results</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

// View Details Offcanvas - RIGHT SIDE OFFCANVAS
const ViewDetailsOffcanvas: React.FC<ViewDetailsOffcanvasProps> = ({ isOpen, onClose, attendance }) => {
    if (!isOpen || !attendance) return null

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={onClose} />
            <div className="fixed right-0 top-0 h-full w-full sm:max-w-2xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden flex flex-col">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Attendance Details</h2>
                        <p className="text-sm text-gray-600">{new Date(attendance.attendance_date).toLocaleDateString()}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded transition-colors">
                        <FiX className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {/* Employee Details */}
                    <div className="mb-4 p-4 bg-blue-50 rounded border border-blue-200">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                                <FiUser className="w-6 h-6 text-blue-800" />
                            </div>
                            <div>
                                <div className="font-semibold text-gray-900">
                                    {attendance.user?.first_name} {attendance.user?.last_name}
                                </div>
                                <div className="text-sm text-gray-600">{attendance.user?.email}</div>
                                {attendance.user?.association_type && (
                                    <div className="text-xs text-gray-500 capitalize">{attendance.user.association_type}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Selfies Section - CIRCULAR */}
                    {(attendance.check_in_selfie || attendance.check_out_selfie) && (
                        <div className="mb-4 p-4 bg-gray-50 rounded">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Selfies</h4>
                            <div className="flex gap-6 justify-center">
                                {attendance.check_in_selfie && (
                                    <div className="flex flex-col items-center">
                                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-green-500 shadow-lg mb-2">
                                            <img
                                                src={attendance.check_in_selfie || "/placeholder.svg"}
                                                alt="Check In Selfie"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <span className="text-xs font-medium text-green-600">Check In</span>
                                    </div>
                                )}
                                {attendance.check_out_selfie && (
                                    <div className="flex flex-col items-center">
                                        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-red-500 shadow-lg mb-2">
                                            <img
                                                src={attendance.check_out_selfie || "/placeholder.svg"}
                                                alt="Check Out Selfie"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <span className="text-xs font-medium text-red-600">Check Out</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Attendance Details - ALL FIELDS */}
                    <div className="mb-4 p-4 bg-gray-50 rounded">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Attendance Details</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">Date:</span>
                                <div className="font-medium text-gray-900">
                                    {new Date(attendance.attendance_date).toLocaleDateString()}
                                </div>
                            </div>
                            <div>
                                <span className="text-gray-600">Marking Type:</span>
                                <div className="font-medium">
                                    <span
                                        className={`px-2 py-1 rounded text-xs ${attendance.marking_type === "auto" ? "bg-blue-100 text-blue-900" : "bg-orange-100 text-orange-700"
                                            }`}
                                    >
                                        {attendance.marking_type}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <span className="text-gray-600">Check In Time:</span>
                                <div className="font-medium text-gray-900">
                                    {attendance.check_in_time ? new Date(attendance.check_in_time).toLocaleTimeString() : "N/A"}
                                </div>
                            </div>
                            <div>
                                <span className="text-gray-600">Check Out Time:</span>
                                <div className="font-medium text-gray-900">
                                    {attendance.check_out_time ? new Date(attendance.check_out_time).toLocaleTimeString() : "N/A"}
                                </div>
                            </div>
                            <div>
                                <span className="text-gray-600">Current Status:</span>
                                <div className="font-medium">
                                    <span
                                        className={`px-2 py-1 rounded-full text-xs ${attendance.work_status === "worked"
                                            ? "bg-green-100 text-green-700"
                                            : attendance.work_status === "pending"
                                                ? "bg-gray-100 text-gray-700"
                                                : "bg-red-100 text-red-700"
                                            }`}
                                    >
                                        {getWorkStatusLabel(normalizeWorkStatus(attendance.work_status))}
                                        {attendance.work_status === "leave" && attendance.leave_type
                                            ? ` (${getAttendanceLeaveTypeLabel(attendance.leave_type)})`
                                            : ""}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <span className="text-gray-600">HR Status:</span>
                                <div className="font-medium">
                                    <span
                                        className={`px-2 py-1 rounded-full text-xs ${getDisplayHRStatusBadgeClasses(attendance.hr_status, attendance.work_status)}`}
                                    >
                                        {getDisplayHRStatusLabel(attendance.hr_status, attendance.work_status)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Location Details — always shown, missing values say so */}
                    <AttendanceLocationDetails record={attendance} className="mb-4 p-4 bg-gray-50 rounded" />

                    {/* Projects */}
                    {attendance.user && (attendance.user as any).user_projects && (attendance.user as any).user_projects.length > 0 && (
                        <div className="mb-4 p-4 bg-gray-50 rounded">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Projects</h4>
                            <div className="text-xs text-gray-900 flex flex-wrap gap-1">
                                {(attendance.user as any).user_projects.map((up: any) => (
                                    <span
                                        key={up.project_id}
                                        className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 max-w-full truncate"
                                        title={up.project?.project_name}
                                    >
                                        {up.project?.project_name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Remarks */}
                    {(attendance.user_remarks || attendance.approver_remarks) && (
                        <div className="mb-4 p-4 bg-gray-50 rounded">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Remarks</h4>
                            <div className="space-y-3 text-sm">
                                {attendance.user_remarks && (
                                    <div>
                                        <span className="text-gray-600">User Remarks:</span>
                                        <div className="text-gray-900 mt-1 p-2 bg-white rounded border border-gray-200">
                                            {attendance.user_remarks}
                                        </div>
                                    </div>
                                )}
                                {attendance.approver_remarks && (
                                    <div>
                                        <span className="text-gray-600">Approver Remarks:</span>
                                        <div className="text-gray-900 mt-1 p-2 bg-white rounded border border-gray-200">
                                            {attendance.approver_remarks}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Approval History */}
                    {attendance.approved_by && attendance.approver && (
                        <div className="mb-4 p-4 bg-green-50 rounded border border-green-200">
                            <h4 className="text-sm font-medium text-green-700 mb-2">Approval History</h4>
                            <div className="text-sm text-gray-700">
                                Approved by {attendance.approver.first_name} {attendance.approver.last_name} on{" "}
                                {attendance.approved_at ? new Date(attendance.approved_at).toLocaleString() : "N/A"}
                            </div>
                        </div>
                    )}

                    {/* Verification — only days that were manually created for a
                        "Not Marked" date carry one. */}
                    {attendance.verification_status && attendance.verification_status !== "not_required" && (
                        <div className="mb-4 p-4 bg-amber-50 rounded border border-amber-200">
                            <h4 className="text-sm font-medium text-amber-800 mb-2">Verification</h4>
                            <div className="text-sm text-gray-700 space-y-1">
                                {attendance.verification_status === "pending" && (
                                    <div>
                                        Awaiting verification
                                        {attendance.verification_pending_with
                                            ? ` by ${attendance.verification_pending_with.first_name} ${attendance.verification_pending_with.last_name}`
                                            : " by the marker's project manager"}
                                        . The day does not count until it is confirmed.
                                    </div>
                                )}
                                {attendance.verification_status === "approved" && (
                                    <div>
                                        Verified
                                        {attendance.verifier
                                            ? ` by ${attendance.verifier.first_name} ${attendance.verifier.last_name}`
                                            : ""}
                                        {attendance.verified_at ? ` on ${new Date(attendance.verified_at).toLocaleString()}` : ""}
                                    </div>
                                )}
                                {attendance.verification_status === "rejected" && (
                                    <div>
                                        Verification rejected
                                        {attendance.verifier
                                            ? ` by ${attendance.verifier.first_name} ${attendance.verifier.last_name}`
                                            : ""}
                                        {attendance.verified_at ? ` on ${new Date(attendance.verified_at).toLocaleString()}` : ""}
                                    </div>
                                )}
                                {attendance.verification_remarks && (
                                    <div className="text-gray-900 mt-1 p-2 bg-white rounded border border-gray-200">
                                        {attendance.verification_remarks}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
                    >
                        Close
                    </button>
                </div>
            </div>
        </>
    )
}

// Attendance Date Offcanvas - UPDATED WITH CIRCULAR SELFIES
const AttendanceDateOffcanvas: React.FC<AttendanceDateOffcanvasProps> = ({
    isOpen,
    onClose,
    date,
    attendances,
    onApprove,
    onManualMark,
    onView,
    readOnly = false,
}) => {
    const { hasPermission } = useAuth()
    const canApproveAttendance =
        !readOnly &&
        (hasPermission("attendance", "approve") ||
            hasPermission("attendance", "manual_mark") ||
            hasPermission("attendance", "view_all"))
    const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined)
    const [calculateModalOpen, setCalculateModalOpen] = useState(false)

    const getWorkStatusBadge = (status: string, leaveType?: string | null) => {
        const config: Record<string, { bg: string; text: string; label: string }> = {
            worked: { bg: "bg-green-100", text: "text-green-700", label: "Worked" },
            idle: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Idle" },
            absent: { bg: "bg-red-100", text: "text-red-700", label: "Absent" },
            leave: { bg: "bg-blue-100", text: "text-blue-900", label: "Leave Applied" },
            weekly_off: { bg: "bg-purple-100", text: "text-purple-700", label: "Weekly Off" },
            holiday: { bg: "bg-indigo-100", text: "text-indigo-700", label: "Holiday" },
            pending: { bg: "bg-gray-100", text: "text-gray-700", label: "Pending Approval" },
        }
        const c = config[status] || config.pending
        const leaveTypeLabel = status === "leave" && leaveType ? ` (${getAttendanceLeaveTypeLabel(leaveType)})` : ""
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>{c.label}{leaveTypeLabel}</span>
    }

    if (!isOpen || !date) return null

    const handleCalculateClick = (userId?: number) => {
        setSelectedUserId(userId)
        setCalculateModalOpen(true)
    }

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={onClose} />
            <div className="overflow-y-auto fixed right-0 top-0 h-full w-full sm:max-w-4xl bg-white shadow-xl z-50 transform transition-transform overflow-hidden">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            Attendance Records -{" "}
                            {date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                        </h2>
                        <p className="text-sm text-gray-600">{attendances.length} record(s) found</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded transition-colors">
                        <FiX className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <div className="p-4">

                    {attendances.length === 0 ? (
                        <div className="text-center py-12">
                            <FiCalendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 text-sm">No attendance records for this date</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {attendances.map((record) => (
                                <div key={record.id} className="border border-gray-200 rounded p-4 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                <FiUser className="w-5 h-5 text-blue-800" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-gray-900">
                                                    {record.user?.first_name} {record.user?.last_name}
                                                </div>
                                                <div className="text-xs text-gray-500">{record.user?.email}</div>
                                                {record.user?.association_type && (
                                                    <div className="text-xs text-gray-500 capitalize">{record.user.association_type}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {getWorkStatusBadge(record.work_status, record.leave_type)}
                                            <VerificationBadge record={record} />
                                            {/* The approve action is for days that still need a
                                                decision. Once a manager has approved the day the
                                                tick is replaced by an "Approved" badge naming who
                                                approved it and when, so a settled day cannot be
                                                re-approved by accident. A day the employee
                                                re-submits goes back to pending and gets its tick
                                                back. */}
                                            {canApproveAttendance && isAttendanceApproved(record) && (
                                                <span
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium"
                                                    title={`Approved by ${getAttendanceApproverName(record)}${record.approved_at ? ` on ${new Date(record.approved_at).toLocaleString()}` : ""}`}
                                                >
                                                    <FiCheck className="w-3 h-3" /> Approved
                                                </span>
                                            )}
                                            {canApproveAttendance && !isAttendanceApproved(record) && (
                                                <button
                                                    onClick={() => onApprove(record.id)}
                                                    className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded"
                                                    title="Approve Attendance"
                                                >
                                                    <FiCheck className="w-4 h-4" />
                                                </button>
                                            )}
                                            {hasPermission('attendance', 'view') && (
                                                <button
                                                    onClick={() => onView(record)}
                                                    className="text-purple-600 hover:text-purple-800 p-1 hover:bg-purple-50 rounded"
                                                    title="View Details"
                                                >
                                                    <FiList className="w-4 h-4" />
                                                </button>
                                            )}
                                            {hasPermission('attendance', 'calculate_payable') && (
                                                <button
                                                    onClick={() => handleCalculateClick(record.user_id)}
                                                    className="text-green-600 hover:text-green-800 p-1 hover:bg-green-50 rounded"
                                                    title="Calculate Payable"
                                                >
                                                    <FaCalculator className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Selfies Section - CIRCULAR */}
                                    {(record.check_in_selfie || record.check_out_selfie) && (
                                        <div className="mb-3 flex gap-4 justify-center bg-gray-50 p-3 rounded">
                                            {record.check_in_selfie && (
                                                <div className="flex flex-col items-center">
                                                    <div className="w-20 h-20 rounded-full overflow-hidden border-3 border-green-500 shadow-md mb-1">
                                                        <img
                                                            src={record.check_in_selfie || "/placeholder.svg"}
                                                            alt="Check In"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                    <span className="text-xs font-medium text-green-600">Check In</span>
                                                </div>
                                            )}
                                            {record.check_out_selfie && (
                                                <div className="flex flex-col items-center">
                                                    <div className="w-20 h-20 rounded-full overflow-hidden border-3 border-red-500 shadow-md mb-1">
                                                        <img
                                                            src={record.check_out_selfie || "/placeholder.svg"}
                                                            alt="Check Out"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                    <span className="text-xs font-medium text-red-600">Check Out</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <div className="text-gray-600 text-xs mb-1">Check In</div>
                                            <div className="flex items-center gap-1">
                                                <FiClock className="w-3 h-3 text-gray-400" />
                                                <span className="font-medium">
                                                    {record.check_in_time ? new Date(record.check_in_time).toLocaleTimeString() : "N/A"}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-gray-600 text-xs mb-1">Check Out</div>
                                            <div className="flex items-center gap-1">
                                                <FiClock className="w-3 h-3 text-gray-400" />
                                                <span className="font-medium">
                                                    {record.check_out_time ? new Date(record.check_out_time).toLocaleTimeString() : "N/A"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <AttendanceLocationDetails
                                        record={record}
                                        className="mt-3 pt-3 border-t border-gray-200"
                                    />

                                    {(record.user_remarks || record.approver_remarks) && (
                                        <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                                            {record.user_remarks && (
                                                <div>
                                                    <div className="text-gray-600 text-xs mb-1">User Remarks</div>
                                                    <div className="text-sm text-gray-700">{record.user_remarks}</div>
                                                </div>
                                            )}
                                            {record.approver_remarks && (
                                                <div>
                                                    <div className="text-gray-600 text-xs mb-1">Approver Remarks</div>
                                                    <div className="text-sm text-gray-700">{record.approver_remarks}</div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
                                        <span
                                            className={`px-2 py-1 rounded ${record.marking_type === "auto" ? "bg-blue-100 text-blue-900" : "bg-orange-100 text-orange-700"}`}
                                        >
                                            {record.marking_type === "auto" ? "Auto" : "Manual"}
                                        </span>
                                        {record.approver && (
                                            <span>
                                                Approved by: {record.approver.first_name} {record.approver.last_name}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <CalculatePayableModal
                isOpen={calculateModalOpen}
                onClose={() => setCalculateModalOpen(false)}
                userId={selectedUserId}
            />
        </>
    )
}

// Leaflet lazy loader (shared across instances)
declare global { interface Window { L?: any } }
let _lfLoaded = false, _lfLoading = false, _lfCbs: (() => void)[] = []
function loadLeaflet(cb: () => void) {
    if (_lfLoaded) { cb(); return }
    _lfCbs.push(cb)
    if (_lfLoading) return
    _lfLoading = true
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
    document.head.appendChild(link)
    const script = document.createElement("script")
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"
    script.onload = () => { _lfLoaded = true; _lfCbs.splice(0).forEach(fn => fn()) }
    document.head.appendChild(script)
}

// Main Component
interface AttendanceManagementProps {
    showTabs?: boolean;
    initialTab?: "direct" | "project" | "shared_projects";
    variant?: "records" | "members";
    defaultViewMode?: "calendar" | "list" | "matrix" | "map";
    allowProjectExport?: boolean;
    hideListActions?: boolean;
    requireProjectAssignment?: boolean;
    mapViewPermission?: { resource: string; action: string };
    /**
     * Data view only: no approving, no changing a status, no marking a day that
     * was never marked. Set for HR > User Attendance, where the HR team reads
     * attendance but the decisions stay with the reporting line. Records can
     * still be opened read-only and exported.
     */
    readOnly?: boolean;
}

export const AttendanceManagement: React.FC<AttendanceManagementProps> = ({
    showTabs = false,
    initialTab = "direct",
    variant = "records",
    defaultViewMode = "matrix",
    allowProjectExport = false,
    hideListActions = false,
    requireProjectAssignment = false,
    mapViewPermission = { resource: "attendance", action: "map_view" },
    readOnly = false,
}) => {
    const { hasPermission, user } = useAuth()
    const density = useAttendanceDensity()
    const stickyColumns = useMemo(
        () => buildStickyLayout(ATTENDANCE_STICKY_COLUMNS, density.px),
        [density]
    )
    const isMembersVariant = variant === "members"
    const pageTitle = isMembersVariant ? "Team Members" : "Attendance Management"
    const pageDescription = isMembersVariant
        ? "View project team members"
        : "View and manage employee attendance records"
    const [viewMode, setViewMode] = useState<"calendar" | "list" | "matrix" | "map">(defaultViewMode)
    const [currentDate, setCurrentDate] = useState(new Date())
    const [attendanceData, setAttendanceData] = useState<AttendanceRecord[]>([])
    const [memberRowsData, setMemberRowsData] = useState<TeamMemberRow[]>([])
    const [filterMembersData, setFilterMembersData] = useState<TeamMemberRow[]>([])
    const [myAssignedProjects, setMyAssignedProjects] = useState<{ project_id: number; project?: { id: number; project_name: string; client_project?: { id: number; client_project_name: string } | null } | null }[]>([])
    // Department groups and roles for the filter panel. Both are small, closed
    // lists, so they are loaded once and listed in full rather than searched
    // for — an empty dropdown reads as "there are none".
    const [departmentGroupOptions, setDepartmentGroupOptions] = useState<SelectOption[]>([])
    const [roleOptions, setRoleOptions] = useState<SelectOption[]>([])
    const [loading, setLoading] = useState(false)
    const [filters, setFilters] = useState<AttendanceFilters>(
        useUrlFilters<AttendanceFilters>({}, URL_FILTER_KEYS),
    )
    const [searchInput, setSearchInput] = useState("")
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
    const [pagination, setPagination] = useState<PaginationType>({
        page: 1,
        limit: 50,
        total_count: 0,
        total_pages: 0,
        offset: 0,
        has_next_page: false,
        has_prev_page: false,
        next_page: null,
        prev_page: null,
    })
    const [memberRowsPage, setMemberRowsPage] = useState(1)
    const [memberRowsLimit, setMemberRowsLimit] = useState(50)
    const [sortConfig, setSortConfig] = useState<AttendanceSortConfig>({
        field: "attendance_date",
        direction: "DESC",
    })

    // Modal states
    const [dateOffcanvasOpen, setDateOffcanvasOpen] = useState(false)
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [selectedDateAttendances, setSelectedDateAttendances] = useState<AttendanceRecord[]>([])
    const [approveModalOpen, setApproveModalOpen] = useState(false)
    const [selectedAttendance, setSelectedAttendance] = useState<AttendanceRecord | null>(null)
    const [viewDetailsOpen, setViewDetailsOpen] = useState(false)
    const [manualMarkModalOpen, setManualMarkModalOpen] = useState(false)
    const [bulkApproveMonthModalOpen, setBulkApproveMonthModalOpen] = useState(false)
    // Set when Manual Mark is opened from a "Not Marked" matrix cell: that cell
    // already names both the employee and the day, so the form should not ask
    // for either again. Cleared on close so the toolbar button still opens a
    // blank form.
    const [manualMarkPreset, setManualMarkPreset] = useState<{ date: string; userId: number } | null>(null)
    const [calculateModalOpen, setCalculateModalOpen] = useState(false)
    const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined)
    const [showExcelModal, setShowExcelModal] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [exportData, setExportData] = useState<Record<string, any>[]>([])
    const [activeTab, setActiveTab] = useState<"direct" | "project" | "shared_projects">(initialTab)
    const [matrixActionKey, setMatrixActionKey] = useState<string | null>(null)
    const [matrixStatusMode, setMatrixStatusMode] = useState<"work_status" | "hr_status">("work_status")

    // Map view state
    const [mapDate, setMapDate] = useState<string>(() => new Date().toISOString().split("T")[0])
    const [mapAttendanceData, setMapAttendanceData] = useState<AttendanceRecord[]>([])
    const [mapLoading, setMapLoading] = useState(false)
    const [leafletReady, setLeafletReady] = useState(false)
    const [showCheckInLayer, setShowCheckInLayer] = useState(true)
    const [showReportingLayer, setShowReportingLayer] = useState(true)
    const [showCheckOutLayer, setShowCheckOutLayer] = useState(true)
    const mapDivRef = useRef<HTMLDivElement>(null)
    const mapContainerRef = useRef<HTMLDivElement>(null)
    const [isMapFullscreen, setIsMapFullscreen] = useState(false)

    const toggleMapFullscreen = () => {
        const el = mapContainerRef.current
        if (!el) return
        if (!document.fullscreenElement) {
            el.requestFullscreen().then(() => setIsMapFullscreen(true)).catch(() => {})
        } else {
            document.exitFullscreen().then(() => setIsMapFullscreen(false)).catch(() => {})
        }
    }

    useEffect(() => {
        const handler = () => setIsMapFullscreen(!!document.fullscreenElement)
        document.addEventListener("fullscreenchange", handler)
        return () => document.removeEventListener("fullscreenchange", handler)
    }, [])
    const leafletMapRef = useRef<any>(null)
    const checkInLayerRef = useRef<any>(null)
    const reportingLayerRef = useRef<any>(null)
    const checkOutLayerRef = useRef<any>(null)
    const isLevelOneUser = user?.role?.level === 1
    // `readOnly` outranks every permission: a read-only screen never approves,
    // never changes a status and never creates a missing day, whatever the
    // caller is otherwise allowed to do elsewhere.
    const canApproveAttendance =
        !readOnly &&
        (isLevelOneUser ||
            hasPermission("attendance", "approve") ||
            hasPermission("attendance", "manual_mark") ||
            hasPermission("attendance", "view_all"))
    // Creating an attendance row that never existed is manual marking, which
    // is a narrower power than approving one that does — a "Not Marked" cell is
    // only clickable for people who actually hold it.
    const canManualMarkAttendance = !readOnly && (isLevelOneUser || hasPermission("attendance", "manual_mark"))
    // Whether TODAY may be manually marked (Masters > Manual Attendance). It
    // starts open, which is also the server's default, so a failed read never
    // hides a day the API would have accepted — the API still has the last word.
    const [manualMarkAllowToday, setManualMarkAllowToday] = useState(true)
    useEffect(() => {
        if (!canManualMarkAttendance) return
        let cancelled = false
        getManualMarkSettings()
            .then((settings) => {
                if (!cancelled) setManualMarkAllowToday(settings?.allow_today !== false)
            })
            .catch(() => {
                /* keep the default */
            })
        return () => {
            cancelled = true
        }
    }, [canManualMarkAttendance])
    const canBulkApproveAttendance = !readOnly && (isLevelOneUser || hasPermission("attendance", "bulk_approve"))
    // Opening a record to look at it is not a decision, so a read-only screen
    // keeps it — that is the whole point of the screen.
    const canViewAttendanceDetails =
        readOnly || canApproveAttendance || hasPermission("attendance", "view")
    const canViewAttendanceMap = hasPermission(mapViewPermission.resource, mapViewPermission.action)
    // Export allowed for: level-1 users always, direct-tab list/matrix views,
    // direct-tab members view, and explicit HR user-attendance screens.
    const canExport =
        isLevelOneUser ||
        (!isMembersVariant && (activeTab !== "project" || allowProjectExport)) ||
        (isMembersVariant && activeTab === "direct")

    // Count active filters
    const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
        if (["user_name", "project_name", "department_name", "role_name"].includes(key) || value === undefined || value === "") return false
        if (isMembersVariant) {
            return key === "user_id" || key === "search" || key === "project_id" || key === "department_id"
        }
        return true
    }).length

    useEffect(() => {
        const handle = setTimeout(() => {
            setFilters((prev) => {
                const nextSearch = searchInput.trim() ? searchInput.trim() : undefined
                if ((prev.search ?? "") === (nextSearch ?? "")) return prev
                return {
                    ...prev,
                    search: nextSearch,
                }
            })
        }, 350)

        return () => clearTimeout(handle)
    }, [searchInput])

    useEffect(() => {
        if (viewMode === "map" && !canViewAttendanceMap) {
            setViewMode(isMembersVariant ? "list" : "matrix")
        }
    }, [canViewAttendanceMap, isMembersVariant, viewMode])

    // Reset filters
    const handleResetFilters = () => {
        setPagination((prev) => ({ ...prev, page: 1 }))
        setSearchInput("")
        setFilters({})
    }

    const fetchFilterMembers = useCallback(async () => {
        try {
            const endpoint = activeTab === "direct"
                ? "/attendance/team-members/direct"
                : "/attendance/team-members/all"
            const [membersRes, projectsRes] = await Promise.all([
                axiosInstance.get(endpoint),
                axiosInstance.get("/users/my-projects"),
            ])
            setFilterMembersData(membersRes.data?.data?.members || [])
            setMyAssignedProjects(projectsRes.data?.data?.projects || [])
        } catch (error) {
            console.error("Error fetching attendance filter members:", error)
            setFilterMembersData([])
        }
    }, [activeTab])

    useEffect(() => {
        let cancelled = false

        const loadFilterLists = async () => {
            try {
                const [groupsRes, rolesRes] = await Promise.all([
                    // Department groups are the departments master (users.department_id),
                    // the same list every report's "Department Group" filter uses —
                    // not client projects. `assigned_only` narrows it the way the
                    // rows are already narrowed: only departments owning one of the
                    // caller's own projects (user_projects → department_projects).
                    // Super admins keep the full list.
                    axiosInstance.get("/departments/list", { params: { page: 1, limit: 500, sort_by: "name", sort_order: "ASC", assigned_only: true } }),
                    axiosInstance.get("/roles/list", { params: { page: 1, limit: 200 } }),
                ])
                if (cancelled) return

                setDepartmentGroupOptions(
                    (groupsRes.data?.data?.departments || []).map((group: any) => ({
                        id: group.id,
                        value: group.id,
                        label: group.name,
                    })),
                )
                setRoleOptions(
                    (rolesRes.data?.data?.roles || []).map((role: any) => ({
                        id: role.id,
                        value: role.id,
                        label: role.name,
                    })),
                )
            } catch (error) {
                console.error("Error fetching attendance filter lists:", error)
            }
        }

        loadFilterLists()
        return () => {
            cancelled = true
        }
    }, [])

    const fetchMapAttendance = useCallback(async (date: string) => {
        setMapLoading(true)
        try {
            const endpoint = activeTab === "direct"
                ? "/attendance/all"
                : activeTab === "shared_projects"
                    ? "/attendance/shared-projects"
                    : "/attendance/project-team"
            const params: any = {
                attendance_date_from: date,
                attendance_date_to: date,
                limit: 500,
                page: 1,
            }
            if (filters.user_id) params.user_id = filters.user_id
            if (filters.project_id) params.project_id = filters.project_id
            if (filters.department_id) params.department_id = filters.department_id
            // The map was built before the role filter existed and never sent it,
            // so picking a role changed nothing on the map.
            if (filters.role_id) params.role_id = filters.role_id
            if (filters.work_status) params.work_status = filters.work_status
            if (filters.hr_status) params.hr_status = filters.hr_status
            if (filters.marking_type) params.marking_type = filters.marking_type
            if (filters.association_type) params.association_type = filters.association_type
            if (filters.search?.trim()) params.search = filters.search.trim()
            if (activeTab === "direct") params.scope = "direct"
            if (requireProjectAssignment) params.require_project_assignment = "true"
            const res = await axiosInstance.get(endpoint, { params })
            setMapAttendanceData(res.data?.data?.attendances || [])
        } catch {
            setMapAttendanceData([])
        } finally {
            setMapLoading(false)
        }
    }, [activeTab, filters, requireProjectAssignment])

    const fetchAttendance = useCallback(async (
        limitValue?: number,
        pageValue?: number,
        sort: AttendanceSortConfig = sortConfig
    ) => {
        if (viewMode === "map") return
        try {
            setLoading(true)
            const effectiveLimit = limitValue ?? pagination.limit ?? 50
            const effectivePage = pageValue ?? pagination.page

            const paramsBase: any = {}
            const year = currentDate.getFullYear()
            const month = currentDate.getMonth()
            const lastDay = new Date(year, month + 1, 0).getDate()
            const monthFrom = `${year}-${String(month + 1).padStart(2, "0")}-01`
            const monthTo = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`

            if (viewMode === "calendar") {
                paramsBase.attendance_date_from = monthFrom
                paramsBase.attendance_date_to = monthTo
            } else if (!isMembersVariant) {
                const shouldDefaultRange = viewMode === "matrix"
                const today = new Date()
                const defaultTo =
                    today.getFullYear() === year && today.getMonth() === month ? formatDateYMD(today) : monthTo

                const from = filters.attendance_date_from || (shouldDefaultRange ? monthFrom : undefined)
                const to = filters.attendance_date_to || (shouldDefaultRange ? defaultTo : undefined)

                if (from) paramsBase.attendance_date_from = from
                if (to) paramsBase.attendance_date_to = to
            }

            if (!isMembersVariant && filters.work_status) paramsBase.work_status = filters.work_status
            if (!isMembersVariant && filters.hr_status) paramsBase.hr_status = filters.hr_status
            if (!isMembersVariant && filters.marking_type) paramsBase.marking_type = filters.marking_type
            if (filters.user_id) paramsBase.user_id = filters.user_id
            if (filters.project_id) paramsBase.project_id = filters.project_id
            if (filters.department_id) paramsBase.department_id = filters.department_id
            if (filters.role_id) paramsBase.role_id = filters.role_id
            if (filters.association_type) paramsBase.association_type = filters.association_type
            if (filters.search?.trim()) paramsBase.search = filters.search.trim()
            if (activeTab === "direct") paramsBase.scope = "direct"
            if (requireProjectAssignment) paramsBase.require_project_assignment = "true"
            if (!isMembersVariant && sort?.field) {
                paramsBase.sort_by = sort.field
                paramsBase.sort_order = sort.direction
            }

            const endpoint = activeTab === "direct"
                ? "/attendance/all"
                : activeTab === "shared_projects"
                    ? "/attendance/shared-projects"
                    : "/attendance/project-team"
            const matrixEndpoint = activeTab === "direct"
                ? "/attendance/all/matrix"
                : activeTab === "shared_projects"
                    ? "/attendance/shared-projects/matrix"
                    : "/attendance/project-team/matrix"
            const membersEndpoint = activeTab === "direct"
                ? "/attendance/team-members/direct"
                : activeTab === "shared_projects"
                    ? "/attendance/team-members/shared-projects"
                    : "/attendance/team-members/all"

            if (isMembersVariant) {
                const response = await axiosInstance.get(membersEndpoint, {
                    params: paramsBase,
                })

                const rows: TeamMemberRow[] = (response.data?.data?.members || []).map((row: TeamMemberRow) => ({
                    ...row,
                    statusesByDate: {},
                }))

                setMemberRowsData(rows)
                setAttendanceData([])
                setPagination((prev) => ({
                    ...prev,
                    total_count: rows.length,
                    total_pages: 1,
                    page: 1,
                }))
                return
            }

            const shouldFetchMatrixRows = viewMode === "matrix"

            if (shouldFetchMatrixRows) {
                const response = await axiosInstance.get(matrixEndpoint, {
                    params: { ...paramsBase, status_mode: matrixStatusMode },
                })

                const isHRMode = matrixStatusMode === "hr_status"
                const workStatusRowsByUserId = new Map<number, Record<string, WorkStatus>>()
                if (isHRMode) {
                    const workStatusResponse = await axiosInstance.get(matrixEndpoint, {
                        params: { ...paramsBase, status_mode: "work_status" },
                    })
                    for (const workStatusRow of workStatusResponse.data?.data?.rows || []) {
                        workStatusRowsByUserId.set(
                            Number(workStatusRow.userId),
                            Object.fromEntries(
                                Object.entries(workStatusRow.statusesByDate || {}).map(([dateStr, status]) => [
                                    dateStr,
                                    normalizeWorkStatus(status),
                                ])
                            ) as Record<string, WorkStatus>
                        )
                    }
                }
                const rows: TeamMemberRow[] = (response.data?.data?.rows || []).map((row: TeamMemberRow) => {
                    const workStatusesByDate = isHRMode ? workStatusRowsByUserId.get(Number(row.userId)) : undefined
                    return {
                        ...row,
                        statusesByDate: Object.fromEntries(
                            Object.entries(row.statusesByDate || {}).map(([dateStr, status]) => [
                                dateStr,
                                isHRMode
                                    ? toHRMatrixStatus(status, workStatusesByDate?.[dateStr])
                                    : normalizeWorkStatus(status),
                            ])
                        ),
                        workStatusesByDate,
                    }
                })

                setMemberRowsData(rows)
                setAttendanceData([])
                setPagination((prev) => ({
                    ...prev,
                    total_count: rows.length,
                    total_pages: 1,
                    page: 1,
                }))
                return
            }

            setMemberRowsData([])

            const response = await axiosInstance.get(endpoint, {
                params: {
                    ...paramsBase,
                    page: effectivePage,
                    limit: effectiveLimit,
                },
            })
            setAttendanceData(response.data.data.attendances || [])
            if (response.data.data.pagination) {
                setPagination((prev) => ({
                    ...prev,
                    total_count: response.data.data.pagination.total_count,
                    total_pages: response.data.data.pagination.total_pages,
                    limit: effectiveLimit,
                }))
            }
            return
        } catch (error) {
            console.error("Error fetching attendance:", error)
        } finally {
            setLoading(false)
        }
    }, [currentDate, filters, pagination.page, pagination.limit, viewMode, activeTab, sortConfig, isMembersVariant, matrixStatusMode]);

    useEffect(() => {
        fetchAttendance()
    }, [fetchAttendance])

    useEffect(() => {
        fetchFilterMembers()
    }, [fetchFilterMembers])

    // Load Leaflet when map view is activated
    useEffect(() => {
        if (viewMode === "map") loadLeaflet(() => setLeafletReady(true))
    }, [viewMode])

    // Fetch attendance data when map view is active
    useEffect(() => {
        if (viewMode === "map") fetchMapAttendance(mapDate)
    }, [viewMode, mapDate, fetchMapAttendance])

    const getMapUserName = (record: AttendanceRecord) => {
        const u = record.user
        return u ? formatName(u.first_name, u.last_name) || u.username || `User ${record.user_id}` : `User ${record.user_id}`
    }

    const getMapMarkerHtml = (color: string, innerSvg: string, name: string) => `
        <div style="display:flex;flex-direction:column;align-items:center;gap:2px;transform:translateY(-2px);">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="22" height="34">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24C24 5.373 18.627 0 12 0z"
              fill="${color}" stroke="#fff" stroke-width="1.5"/>
            ${innerSvg}
          </svg>
          <span style="background:#fff;border:1px solid #d1d5db;border-radius:4px;padding:1px 4px;font-size:10px;font-weight:700;color:#111827;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,.18);">${escapeHtml(name)}</span>
        </div>
    `

    // Initialize Leaflet map when ready
    useEffect(() => {
        if (viewMode !== "map" || !leafletReady || !mapDivRef.current) return
        const L = window.L
        if (!L) return
        if (!leafletMapRef.current) {
            leafletMapRef.current = L.map(mapDivRef.current, { center: [20.5937, 78.9629], zoom: 5 })
            L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
                maxZoom: 19,
            }).addTo(leafletMapRef.current)
            checkInLayerRef.current = L.layerGroup().addTo(leafletMapRef.current)
            reportingLayerRef.current = L.layerGroup().addTo(leafletMapRef.current)
            checkOutLayerRef.current = L.layerGroup().addTo(leafletMapRef.current)
        }
        setTimeout(() => leafletMapRef.current?.invalidateSize(), 200)
    }, [viewMode, leafletReady])

    // Destroy map on unmount or view change away from map
    useEffect(() => {
        if (viewMode !== "map" && leafletMapRef.current) {
            leafletMapRef.current.remove()
            leafletMapRef.current = null
            checkInLayerRef.current = null
            reportingLayerRef.current = null
            checkOutLayerRef.current = null
        }
    }, [viewMode])

    // Check-in layer
    useEffect(() => {
        if (viewMode !== "map" || !leafletMapRef.current || !checkInLayerRef.current) return
        const L = window.L
        if (!L) return
        checkInLayerRef.current.clearLayers()
        if (!showCheckInLayer) return
        const bounds: [number, number][] = []
        mapAttendanceData.forEach(record => {
            const lat = record.check_in_latitude, lng = record.check_in_longitude
            if (!lat || !lng || (lat === 0 && lng === 0)) return
            const icon = L.divIcon({
                className: "",
                html: getMapMarkerHtml("#059669", `<path d="M7.5 12.5l3 3 6-6" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`, getMapUserName(record)),
                iconSize: [120, 52], iconAnchor: [60, 34], popupAnchor: [0, -34],
            })
            const name = getMapUserName(record)
            const marker = L.marker([lat, lng], { icon }).addTo(checkInLayerRef.current)
            marker.bindPopup(`<div style="min-width:160px"><b>${escapeHtml(name)}</b><br/>Check-in: ${escapeHtml(record.check_in_time || '-')}<br/>${escapeHtml(record.check_in_address || '')}</div>`, { maxWidth: 260 })
            bounds.push([lat, lng])
        })
        if (bounds.length > 0) leafletMapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })
    }, [viewMode, showCheckInLayer, mapAttendanceData, leafletReady])

    // Reporting layer
    useEffect(() => {
        if (viewMode !== "map" || !leafletMapRef.current || !reportingLayerRef.current) return
        const L = window.L
        if (!L) return
        reportingLayerRef.current.clearLayers()
        if (!showReportingLayer) return
        mapAttendanceData.forEach(record => {
            const lat = Number(record.user?.reporting_latitude ?? record.reporting_latitude)
            const lng = Number(record.user?.reporting_longitude ?? record.reporting_longitude)
            if (!lat || !lng || (lat === 0 && lng === 0)) return
            const icon = L.divIcon({
                className: "",
                html: `<div style="display:flex;flex-direction:column;align-items:center;gap:2px;">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32">
                    <circle cx="16" cy="16" r="14" fill="#9333ea" stroke="#fff" stroke-width="2.5"/>
                    <path d="M16 7L8 14h3v9h4v-5h2v5h4v-9h3L16 7z" fill="#fff"/>
                  </svg>
                  <span style="background:#fff;border:1px solid #d1d5db;border-radius:4px;padding:1px 4px;font-size:10px;font-weight:700;color:#111827;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,.18);">${escapeHtml(getMapUserName(record))}</span>
                </div>`,
                iconSize: [120, 50], iconAnchor: [60, 16], popupAnchor: [0, -18],
            })
            const name = getMapUserName(record)
            const marker = L.marker([lat, lng], { icon }).addTo(reportingLayerRef.current)
            marker.bindPopup(`<div style="min-width:160px"><b>${escapeHtml(name)}</b><br/>Reporting location</div>`, { maxWidth: 260 })
        })
    }, [viewMode, showReportingLayer, mapAttendanceData, leafletReady])

    // Check-out layer
    useEffect(() => {
        if (viewMode !== "map" || !leafletMapRef.current || !checkOutLayerRef.current) return
        const L = window.L
        if (!L) return
        checkOutLayerRef.current.clearLayers()
        if (!showCheckOutLayer) return
        mapAttendanceData.forEach(record => {
            const lat = record.check_out_latitude, lng = record.check_out_longitude
            if (!lat || !lng || (lat === 0 && lng === 0)) return
            const icon = L.divIcon({
                className: "",
                html: getMapMarkerHtml("#e11d48", `<path d="M12 8v7M9 13l3 3 3-3" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>`, getMapUserName(record)),
                iconSize: [120, 52], iconAnchor: [60, 34], popupAnchor: [0, -34],
            })
            const name = getMapUserName(record)
            const marker = L.marker([lat, lng], { icon }).addTo(checkOutLayerRef.current)
            marker.bindPopup(`<div style="min-width:160px"><b>${escapeHtml(name)}</b><br/>Check-out: ${escapeHtml(record.check_out_time || '-')}<br/>${escapeHtml(record.check_out_address || '')}</div>`, { maxWidth: 260 })
        })
    }, [viewMode, showCheckOutLayer, mapAttendanceData, leafletReady])

    const getCalendarDays = (): CalendarDay[] => {
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth()

        const firstDay = new Date(year, month, 1)
        const lastDay = new Date(year, month + 1, 0)
        const daysInMonth = lastDay.getDate()
        const startingDayOfWeek = firstDay.getDay()

        const days: CalendarDay[] = []

        const prevMonthLastDay = new Date(year, month, 0).getDate()
        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            days.push({
                date: new Date(year, month - 1, prevMonthLastDay - i),
                isCurrentMonth: false,
                attendances: [],
            })
        }

        for (let i = 1; i <= daysInMonth; i++) {
            const date = new Date(year, month, i)
            // Format date as YYYY-MM-DD without timezone conversion
            const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`
            const dayAttendances = attendanceData.filter((a) => a.attendance_date === dateString)

            days.push({
                date,
                isCurrentMonth: true,
                attendances: dayAttendances,
            })
        }

        const remainingDays = 42 - days.length
        for (let i = 1; i <= remainingDays; i++) {
            days.push({
                date: new Date(year, month + 1, i),
                isCurrentMonth: false,
                attendances: [],
            })
        }

        return days
    }

    const handleDateClick = (day: CalendarDay) => {
        if (!day.isCurrentMonth) return
        setSelectedDate(day.date)
        setSelectedDateAttendances(day.attendances)
        setDateOffcanvasOpen(true)
    }

    const handleApproveClick = (attendanceId: number) => {
        const attendance = attendanceData.find((a) => a.id === attendanceId)
        if (attendance) {
            setSelectedAttendance(attendance)
            setApproveModalOpen(true)
            setDateOffcanvasOpen(false)
        }
    }

    const fetchAttendanceRecordForDate = useCallback(async (userId: number, attendanceDate: string) => {
        const endpoint = activeTab === "direct"
            ? "/attendance/all"
            : activeTab === "shared_projects"
                ? "/attendance/shared-projects"
                : "/attendance/project-team"
        const response = await axiosInstance.get(endpoint, {
            params: {
                user_id: userId,
                attendance_date_from: attendanceDate,
                attendance_date_to: attendanceDate,
                page: 1,
                limit: 1,
            },
        })

        const records: AttendanceRecord[] = response.data?.data?.attendances || []
        return records[0] ?? null
    }, [activeTab])

    const handleMatrixCellClick = useCallback(async (userId: number, attendanceDate: string) => {
        const actionKey = `${userId}-${attendanceDate}`

        try {
            setMatrixActionKey(actionKey)
            const attendance = await fetchAttendanceRecordForDate(userId, attendanceDate)
            if (!attendance) return

            setSelectedAttendance(attendance)
            // Approvers can re-open the approve modal even after approval so the
            // status can be changed anytime — don't gate on approved_by.
            if (canApproveAttendance) {
                setApproveModalOpen(true)
                return
            }

            if (canViewAttendanceDetails) {
                setViewDetailsOpen(true)
            }
        } catch (error) {
            console.error("Error fetching attendance for matrix action:", error)
        } finally {
            setMatrixActionKey(null)
        }
    }, [canApproveAttendance, canViewAttendanceDetails, fetchAttendanceRecordForDate])

    /**
     * A day with no attendance row at all. There is nothing to open, so this
     * offers to create it: Manual Mark, with the cell's employee and date
     * already chosen.
     */
    const handleMatrixNotMarkedClick = useCallback((userId: number, attendanceDate: string) => {
        setManualMarkPreset({ date: attendanceDate, userId })
        setManualMarkModalOpen(true)
    }, [])

    const handleCalculateClick = (userId?: number) => {
        setSelectedUserId(userId)
        setCalculateModalOpen(true)
    }

    const getWorkStatusBadge = (status: string, leaveType?: string | null) => {
        const config: Record<string, { bg: string; text: string }> = {
            worked: { bg: "bg-green-100", text: "text-green-800" },
            idle: { bg: "bg-yellow-100", text: "text-yellow-800" },
            absent: { bg: "bg-red-100", text: "text-red-800" },
            leave: { bg: "bg-blue-100", text: "text-blue-900" },
            weekly_off: { bg: "bg-purple-100", text: "text-purple-700" },
            holiday: { bg: "bg-indigo-100", text: "text-indigo-700" },
            work_from_home: { bg: "bg-teal-100", text: "text-teal-800" },
            pending: { bg: "bg-gray-100", text: "text-gray-700" },
        }
        const c = config[status] || config.pending
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
                {status === "leave"
                    ? `Leave Applied${leaveType ? ` (${getAttendanceLeaveTypeLabel(leaveType)})` : ""}`
                    : status === "pending" ? "Pending Approval" : status.replace(/_/g, " ")}
            </span>
        )
    }

    const handleSort = (field: string) => {
        const direction = sortConfig.field === field && sortConfig.direction === "ASC" ? "DESC" : "ASC"
        const newSort = { field, direction } as AttendanceSortConfig
        setSortConfig(newSort)
        fetchAttendance(pagination.limit, pagination.page, newSort)
    }

    const getSortIcon = (field: string) => {
        if (sortConfig.field !== field) return <FaSort className="w-3 h-3 text-gray-400" />
        return sortConfig.direction === "ASC" ? (
            <FaSortUp className="w-3 h-3 text-blue-800" />
        ) : (
            <FaSortDown className="w-3 h-3 text-blue-800" />
        )
    }

    const handlePageChange = (page: number) => {
        setPagination((prev) => ({ ...prev, page }))
        fetchAttendance(pagination.limit, page, sortConfig)
    }

    const handleLimitChange = (limit: number) => {
        setPagination((prev) => ({ ...prev, limit, page: 1 }))
        fetchAttendance(limit, 1, sortConfig)
    }

    const formatDateValue = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : "")

    const formatDateTimeValue = (value?: string | null) => (value ? new Date(value).toLocaleString() : "")

    const buildExportParams = () => {
        const params: Record<string, any> = {}

        if (isMembersVariant) {
            if (filters.user_id) params.user_id = filters.user_id
            if (filters.search?.trim()) params.search = filters.search.trim()
            return params
        }

        if (viewMode === "calendar") {
            const year = currentDate.getFullYear()
            const month = currentDate.getMonth()
            const lastDay = new Date(year, month + 1, 0).getDate()
            params.attendance_date_from = `${year}-${String(month + 1).padStart(2, "0")}-01`
            params.attendance_date_to = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`
        } else {
            const year = currentDate.getFullYear()
            const month = currentDate.getMonth()
            const lastDay = new Date(year, month + 1, 0).getDate()
            const monthFrom = `${year}-${String(month + 1).padStart(2, "0")}-01`
            const monthTo = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`
            const today = new Date()
            const defaultTo =
                today.getFullYear() === year && today.getMonth() === month ? formatDateYMD(today) : monthTo
            const shouldDefaultRange = viewMode === "matrix"

            if (filters.attendance_date_from) {
                params.attendance_date_from = filters.attendance_date_from
            } else if (shouldDefaultRange) {
                params.attendance_date_from = monthFrom
            }

            if (filters.attendance_date_to) {
                params.attendance_date_to = filters.attendance_date_to
            } else if (shouldDefaultRange) {
                params.attendance_date_to = defaultTo
            }
        }

        if (filters.work_status) params.work_status = filters.work_status
        if (filters.hr_status) params.hr_status = filters.hr_status
        if (filters.marking_type) params.marking_type = filters.marking_type
        if (filters.user_id) params.user_id = filters.user_id
        if (filters.project_id) params.project_id = filters.project_id
        if (filters.department_id) params.department_id = filters.department_id
        if (filters.role_id) params.role_id = filters.role_id
        if (filters.association_type) params.association_type = filters.association_type
        if (filters.search?.trim()) params.search = filters.search.trim()

        if (sortConfig.field) {
            params.sort_by = sortConfig.field
            params.sort_order = sortConfig.direction
        }

        return params
    }

    const mapAttendanceForExport = (record: AttendanceRecord) => {
        const employeeName = `${record.user?.first_name ?? ""} ${record.user?.last_name ?? ""}`.trim() || "N/A"
        const approverName = record.approver
            ? `${record.approver.first_name ?? ""} ${record.approver.last_name ?? ""}`.trim()
            : ""
        const reportingPerson = record.user?.project_manager
            ? `${record.user.project_manager.first_name ?? ""} ${record.user.project_manager.last_name ?? ""}`.trim()
            : ""
        const aerialDistanceKm = calculateAerialDistanceKm(
            record.reporting_latitude,
            record.reporting_longitude,
            record.check_in_latitude,
            record.check_in_longitude
        )

        const distanceInKm = typeof record.distance_from_reporting === "number"
            ? (record.distance_from_reporting / 1000).toFixed(2)
            : ""
        const checkoutDistanceInKm = typeof record.check_out_distance_from_reporting === "number"
            ? (record.check_out_distance_from_reporting / 1000).toFixed(2)
            : ""

        return {
            "Attendance Date": formatDateValue(record.attendance_date),
            "Employee Name": employeeName,
            "Employee Code": record.user?.employee_code ?? "",
            "Username": record.user?.username ?? "",
            "Role": record.user?.role?.name ?? "",
            "Designation": record.user?.designation?.name ?? "",
            "Department/Project Group": getUserDepartmentOrProjectGroup(record.user),
            "App Version": record.user?.device_version ?? "",
            "Circle Name": record.user?.state?.circle_name || record.user?.state?.state_name || "",
            "Employee Email": record.user?.email ?? "",
            "Association Type": getAssociationDisplayLabel(record.user?.association_type ?? ""),
            "Reporting Person": reportingPerson,
            "Marking Type": record.marking_type ?? "",
            "Work Status": record.work_status ? getWorkStatusLabel(normalizeWorkStatus(record.work_status)) : "",
            "Leave Type": record.work_status === "leave" ? getAttendanceLeaveTypeLabel(record.leave_type) : "",
            "HR Status": record.hr_status ? getDisplayHRStatusLabel(record.hr_status, record.work_status) : "",
            "Worked Hours": record.is_auto_checkout ? "NA" : record.worked_hours ?? "",
            "Check In Time": formatDateTimeValue(record.check_in_time),
            "Check Out Time": formatDateTimeValue(record.check_out_time),
            "Check In Address": record.check_in_address ?? "",
            "Check Out Address": record.check_out_address ?? "",
            "Check In Latitude": record.check_in_latitude ?? "",
            "Check In Longitude": record.check_in_longitude ?? "",
            "Check Out Latitude": record.check_out_latitude ?? "",
            "Check Out Longitude": record.check_out_longitude ?? "",
            "Check In Selfie URL": record.check_in_selfie ?? "",
            "Check Out Selfie URL": record.check_out_selfie ?? "",
            "Checkin Distance (km)": distanceInKm,
            "Distance Checkout (km)": checkoutDistanceInKm,
            "Aerial Distance from User to Check In (km)": aerialDistanceKm?.toFixed(2) ?? "",
            "User Remarks": record.user_remarks ?? "",
            "Approver Remarks": record.approver_remarks ?? "",
            "Approved By": approverName,
            "Approved At": formatDateTimeValue(record.approved_at),
            "Created At": formatDateTimeValue(record.created_at),
            "Updated At": formatDateTimeValue(record.updated_at),
        }
    }

    const fetchExportData = async () => {
        if (!canExport) {
            return
        }

        try {
            setExporting(true)
            const baseParams = buildExportParams()

            const filterMemberRows = (rows: TeamMemberRow[]) => {
                let filtered = rows

                if (filters.user_id) {
                    const userId = Number(filters.user_id)
                    filtered = filtered.filter((r) => r.userId === userId)
                }

                if (filters.project_id) {
                    const projectId = Number(filters.project_id)
                    filtered = filtered.filter((r) =>
                        (r.user?.user_projects || []).some((up) => Number(up.project?.id) === projectId)
                    )
                }

                if (filters.department_id) {
                    const departmentId = Number(filters.department_id)
                    filtered = filtered.filter((r) => Number(r.user?.department?.id ?? (r.user as any)?.department_id) === departmentId)
                }

                if (!isMembersVariant && filters.work_status) {
                    const target = normalizeWorkStatus(filters.work_status)
                    filtered = filtered.filter((r) => Object.values(r.statusesByDate).some((s) => s === target))
                }

                if (filters.association_type) {
                    filtered = filtered.filter((r) => String(r.user?.association_type || "").toLowerCase() === filters.association_type)
                }

                if (filters.search?.trim()) {
                    const q = filters.search.trim().toLowerCase()
                    filtered = filtered.filter((r) => {
                        const u = r.user
                        const reportingPerson = formatName(u?.project_manager?.first_name, u?.project_manager?.last_name)
                        const hay = [
                            r.userId,
                            u?.employee_code,
                            u?.username,
                            formatName(u?.first_name, u?.last_name),
                            u?.email,
                            u?.designation?.name,
                            u?.role?.name,
                            u?.association_type,
                            getUserDepartmentOrProjectGroup(u),
                            u?.device_version,
                            u?.state?.state_name,
                            getUserDistrictName(u),
                            reportingPerson,
                            getUserProjectNames(u),
                        ]
                            .filter((v) => v !== undefined && v !== null && String(v).trim() !== "")
                            .join(" ")
                            .toLowerCase()

                        return hay.includes(q)
                    })
                }

                return filtered
            }

            const mapMemberRowForExport = (row: TeamMemberRow, includeDates: boolean) => {
                const u = row.user
                const reportingPerson = formatName(u?.project_manager?.first_name, u?.project_manager?.last_name) || "N/A"
                const username = u?.username || formatName(u?.first_name, u?.last_name) || "N/A"

                const base: Record<string, any> = {
                    "E-Code": u?.employee_code ?? "N/A",
                    Username: username,
                    Userid: row.userId,
                    Designation: u?.designation?.name ?? "N/A",
                    Role: u?.role?.name ?? "N/A",
                    Association: getAssociationDisplayLabel(u?.association_type ?? "N/A"),
                    "Reporting Person": reportingPerson,
                    "Reporting State": u?.state?.state_name ?? "N/A",
                    "Reporting District": getUserDistrictName(u),
                    "Project Names": getUserProjectNames(u),
                    "Department/Project Group": getUserDepartmentOrProjectGroup(u),
                    "App Version": u?.device_version ?? "N/A",
                }

                if (!includeDates) return base

                const datesForExport = Array.isArray(matrixDatesDesc) ? matrixDatesDesc : []
                const isHRExportMode = matrixStatusMode === "hr_status"
                for (const dateStr of datesForExport) {
                    const exportStatus = row.statusesByDate[dateStr] ?? (isHRExportMode ? HRStatus.NOT_MARKED : WorkStatus.NOT_MARKED)
                    base[dateStr] = getMatrixCellLabel(exportStatus, isHRExportMode, row.leaveTypesByDate?.[dateStr])
                }

                // Append the summary count columns shown at the end of the matrix
                // so the exported Excel matches the on-screen table.
                if (isHRExportMode) {
                    const c = countHRMatrixRow(row.statusesByDate, row.workStatusesByDate, datesForExport, row.leaveTypesByDate)
                    base["Present"] = c.present
                    base["Absent"] = c.absent
                    base["Pending Approval"] = c.pending
                    base["Not Marked"] = c.not_marked
                    base["Leave Applied"] = c.leave_applied
                    for (const option of ATTENDANCE_LEAVE_TYPE_OPTIONS) {
                        base[`${option.label} Leave`] = c.leave_by_type[option.value]
                    }
                    base["Working Days"] = c.working_days
                } else {
                    const c = countWorkMatrixRow(row.statusesByDate, datesForExport)
                    base["Present"] = c.present
                    base["Worked"] = c.worked
                    base["Late"] = c.late
                    base["WFH"] = c.wfh
                    base["Idle"] = c.idle
                    base["W/Off"] = c.weekly_off
                    base["Holiday"] = c.holiday
                    base["Absent"] = c.absent
                    base["Pending Approval"] = c.pending
                    base["Not Marked"] = c.not_marked
                    base["Working Days"] = c.working_days
                    base["Leave Applied"] = c.leave
                }

                return base
            }

            if (viewMode === "matrix") {
                const endpoint = activeTab === "direct"
                    ? "/attendance/all/matrix"
                    : activeTab === "shared_projects"
                        ? "/attendance/shared-projects/matrix"
                        : "/attendance/project-team/matrix"
                const response = await axiosInstance.get(endpoint, { params: { ...baseParams, status_mode: matrixStatusMode } })
                const isHRModeExport = matrixStatusMode === "hr_status"
                // Leave Applied cells and Working Days need the work status of
                // each day, which the HR-status matrix does not carry — same
                // second fetch the on-screen matrix makes.
                const workStatusRowsByUserId = new Map<number, Record<string, WorkStatus>>()
                if (isHRModeExport) {
                    const workStatusResponse = await axiosInstance.get(endpoint, {
                        params: { ...baseParams, status_mode: "work_status" },
                    })
                    for (const workStatusRow of workStatusResponse.data?.data?.rows || []) {
                        workStatusRowsByUserId.set(
                            Number(workStatusRow.userId),
                            Object.fromEntries(
                                Object.entries(workStatusRow.statusesByDate || {}).map(([dateStr, status]) => [
                                    dateStr,
                                    normalizeWorkStatus(status),
                                ])
                            ) as Record<string, WorkStatus>
                        )
                    }
                }
                const rows: TeamMemberRow[] = filterMemberRows(
                    (response.data?.data?.rows || []).map((row: TeamMemberRow) => {
                        const workStatusesByDate = isHRModeExport ? workStatusRowsByUserId.get(Number(row.userId)) : undefined
                        return {
                            ...row,
                            statusesByDate: Object.fromEntries(
                                Object.entries(row.statusesByDate || {}).map(([dateStr, status]) => [
                                    dateStr,
                                    isHRModeExport
                                        ? toHRMatrixStatus(status, workStatusesByDate?.[dateStr])
                                        : normalizeWorkStatus(status),
                                ])
                            ),
                            workStatusesByDate,
                        }
                    })
                )
                setExportData(rows.map((r) => mapMemberRowForExport(r, true)))
            } else if (isMembersVariant) {
                const endpoint = activeTab === "direct"
                    ? "/attendance/team-members/direct"
                    : "/attendance/team-members/all"
                const response = await axiosInstance.get(endpoint, { params: baseParams })
                const rows: TeamMemberRow[] = filterMemberRows(
                    (response.data?.data?.members || []).map((row: TeamMemberRow) => ({
                        ...row,
                        statusesByDate: {},
                    }))
                )
                setExportData(rows.map((r) => mapMemberRowForExport(r, false)))
            } else {
                const limit = 50 // API rejects larger page sizes; chunk exports to stay within safe limits
                let page = 1
                let totalPages = 1
                const allRecords: AttendanceRecord[] = []

                do {
                    const params = {
                        ...baseParams,
                        page,
                        limit,
                    }

                    const endpoint = activeTab === "direct"
                        ? "/attendance/all"
                        : activeTab === "shared_projects"
                            ? "/attendance/shared-projects"
                            : "/attendance/project-team"
                    const response = await axiosInstance.get(endpoint, { params })
                    const responseData = response.data?.data
                    const attendances: AttendanceRecord[] = responseData?.attendances || []
                    allRecords.push(...attendances)

                    const paginationInfo = responseData?.pagination
                    if (paginationInfo) {
                        const derivedTotalPages =
                            paginationInfo.total_pages ??
                            paginationInfo.totalPages ??
                            (paginationInfo.total_count ? Math.ceil(paginationInfo.total_count / limit) : undefined)
                        totalPages = derivedTotalPages || totalPages
                    } else if (attendances.length < limit) {
                        totalPages = page
                    }

                    page += 1
                } while (page <= totalPages)

                setExportData(allRecords.map(mapAttendanceForExport))
            }
            setShowExcelModal(true)
        } catch (error) {
            console.error("Error exporting attendance data:", error)
        } finally {
            setExporting(false)
        }
    }

      const CalendarView = () => {
          const calendarDays = getCalendarDays()
          const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

        return (
            <div className="bg-white rounded shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                        </h3>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
                                className="p-2 hover:bg-gray-100 rounded transition-colors"
                            >
                                <FiChevronLeft className="w-5 h-5 text-gray-600" />
                            </button>
                            <button
                                onClick={() => setCurrentDate(new Date())}
                                className="px-3 py-1 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors"
                            >
                                Today
                            </button>
                            <button
                                onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
                                className="p-2 hover:bg-gray-100 rounded transition-colors"
                            >
                                <FiChevronRight className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-4">
                    <div className="grid grid-cols-7 gap-2 mb-2">
                        {weekDays.map((day) => (
                            <div key={day} className="text-center text-xs font-medium text-gray-600 py-2">
                                {day}
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-2">
                        {calendarDays.map((day, index) => {
                            const isToday = day.date.toDateString() === new Date().toDateString()
                            const isSunday = day.date.getDay() === 0
                            const hasAttendance = day.attendances.length > 0

                            return (
                                <div
                                    key={index}
                                    onClick={() => handleDateClick(day)}
                                    className={`min-h-[100px] border rounded p-2 cursor-pointer transition-all ${day.isCurrentMonth ? "bg-white hover:bg-blue-50" : "bg-gray-50"
                                        } ${isToday ? "border-blue-500 border-2" : "border-gray-200"} ${isSunday && day.isCurrentMonth ? "bg-red-50" : ""
                                        } ${hasAttendance && day.isCurrentMonth ? "border-green-300" : ""}`}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span
                                            className={`text-sm font-medium ${day.isCurrentMonth ? "text-gray-900" : "text-gray-400"
                                                } ${isToday ? "text-blue-800 font-bold" : ""}`}
                                        >
                                            {day.date.getDate()}
                                        </span>
                                        {isSunday && day.isCurrentMonth && <span className="text-xs text-red-600 font-medium">OFF</span>}
                                    </div>

                                    {hasAttendance && day.isCurrentMonth && (
                                        <div className="space-y-1">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-gray-600">{day.attendances.length} entries</span>
                                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                            </div>
                                            {day.attendances.slice(0, 2).map((att, idx) => (
                                                <div key={idx} className="text-xs truncate text-gray-700">
                                                    {att.user?.first_name}
                                                </div>
                                            ))}
                                            {day.attendances.length > 2 && (
                                                <div className="text-xs text-blue-800 font-medium">+{day.attendances.length - 2} more</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>
          )
      }

      const matrixRange = useMemo(() => {
          const year = currentDate.getFullYear()
          const month = currentDate.getMonth()
          const lastDay = new Date(year, month + 1, 0).getDate()
          const monthFrom = `${year}-${String(month + 1).padStart(2, "0")}-01`
          const monthTo = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`

          const today = new Date()
          const defaultTo = today.getFullYear() === year && today.getMonth() === month ? formatDateYMD(today) : monthTo

          return {
              from: filters.attendance_date_from || monthFrom,
              to: filters.attendance_date_to || defaultTo,
          }
      }, [currentDate, filters.attendance_date_from, filters.attendance_date_to])

      const matrixDatesDesc = useMemo(() => {
          const asc = buildDateRange(matrixRange.from, matrixRange.to)
          return asc.reverse()
      }, [matrixRange.from, matrixRange.to])

      const memberRows = useMemo(() => memberRowsData, [memberRowsData])
      const availableFilterMembers = useMemo(() => {
          const map = new Map<number, TeamMemberRow>()
          for (const row of filterMembersData) {
              if (!map.has(row.userId)) {
                  map.set(row.userId, row)
              }
          }
          return Array.from(map.values())
      }, [filterMembersData])

      const projectFilterOptions = useMemo<SelectOption[]>(() => {
          const map = new Map<number, SelectOption>()
          myAssignedProjects.forEach((up) => {
              if (up.project?.id && up.project?.project_name && !map.has(up.project.id)) {
                  map.set(up.project.id, {
                      id: up.project.id,
                      value: up.project.id,
                      label: up.project.project_name,
                  })
              }
          })
          return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label))
      }, [myAssignedProjects])


      const filteredMemberRows = useMemo(() => {
          let rows = memberRows

          if (filters.user_id) {
              const userId = Number(filters.user_id)
              rows = rows.filter((r) => r.userId === userId)
          }

          if (filters.project_id) {
              const projectId = Number(filters.project_id)
              rows = rows.filter((r) =>
                  (r.user?.user_projects || []).some((up) => Number(up.project?.id) === projectId)
              )
          }

          if (filters.department_id) {
              const departmentId = Number(filters.department_id)
              rows = rows.filter((r) => Number(r.user?.department?.id ?? (r.user as any)?.department_id) === departmentId)
          }

          if (!isMembersVariant && filters.work_status) {
              const target = normalizeWorkStatus(filters.work_status)
              rows = rows.filter((r) => Object.values(r.statusesByDate).some((s) => s === target))
          }

          if (filters.association_type) {
              rows = rows.filter((r) => String(r.user?.association_type || "").toLowerCase() === filters.association_type)
          }

          if (filters.search?.trim()) {
              const q = filters.search.trim().toLowerCase()
              rows = rows.filter((r) => {
                  const u = r.user
                  const reportingPerson = formatName(u?.project_manager?.first_name, u?.project_manager?.last_name)
                  const hay = [
                      r.userId,
                      u?.employee_code,
                      u?.username,
                      formatName(u?.first_name, u?.last_name),
                      u?.email,
                      u?.designation?.name,
                      u?.role?.name,
                      u?.association_type,
                      getUserDepartmentOrProjectGroup(u),
                      u?.device_version,
                      u?.state?.state_name,
                      getUserDistrictName(u),
                      reportingPerson,
                      getUserProjectNames(u),
                  ]
                      .filter((v) => v !== undefined && v !== null && String(v).trim() !== "")
                      .join(" ")
                      .toLowerCase()

                  return hay.includes(q)
              })
          }

          return rows
      }, [filters.association_type, filters.department_id, filters.project_id, filters.search, filters.user_id, filters.work_status, memberRows, isMembersVariant])

      const uniqueUserCount = filteredMemberRows.length

      // The matrix and members tables are one row per employee and used to
      // render every employee at once. They are paged here, on the client: the
      // matrix endpoint returns the whole team in one response (the row set is
      // also what export and the summary counts read), so only the rendering is
      // split into pages.
      const memberTotalPages = Math.max(1, Math.ceil(uniqueUserCount / memberRowsLimit))
      const currentMemberPage = Math.min(memberRowsPage, memberTotalPages)
      const pagedMemberRows = useMemo(
          () => filteredMemberRows.slice((currentMemberPage - 1) * memberRowsLimit, currentMemberPage * memberRowsLimit),
          [filteredMemberRows, currentMemberPage, memberRowsLimit],
      )
      const memberRowsPagination: PaginationType = {
          page: currentMemberPage,
          limit: memberRowsLimit,
          total_count: uniqueUserCount,
          total_pages: memberTotalPages,
          offset: (currentMemberPage - 1) * memberRowsLimit,
          has_next_page: currentMemberPage < memberTotalPages,
          has_prev_page: currentMemberPage > 1,
          next_page: currentMemberPage < memberTotalPages ? currentMemberPage + 1 : null,
          prev_page: currentMemberPage > 1 ? currentMemberPage - 1 : null,
      }

      // Any change to what is listed starts again from the first page.
      useEffect(() => {
          setMemberRowsPage(1)
      }, [filters, activeTab, viewMode, currentDate])

      const MembersListView = () => {
          const tableColumnsCount = 12

          return (
              <div className="bg-white rounded shadow-sm border border-gray-200">
                  <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-30">
                              <tr>
                                  {stickyColumns.map((column) => (
                                      <th
                                          key={column.key}
                                          style={column.style}
                                          className={`sticky z-30 ${density.headCell} text-left font-medium text-gray-600 uppercase bg-gray-50 border-r border-gray-200`}
                                      >
                                          {column.label}
                                      </th>
                                  ))}
                                  <th style={{ minWidth: density.px(120) }} className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Reporting State</th>
                                  <th style={{ minWidth: density.px(120) }} className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Reporting District</th>
                                  <th style={{ minWidth: density.px(200) }} className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Project Names</th>
                                  <th style={{ minWidth: density.px(150) }} className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Department/Project Group</th>
                                  <th style={{ minWidth: density.px(100) }} className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>App Version</th>
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                              {loading ? (
                                  <tr>
                                      <td colSpan={tableColumnsCount} className={`${density.placeholderCell} text-center`}>
                                          <div className="flex flex-col items-center">
                                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800 mb-3"></div>
                                              <span className="text-sm text-gray-600">Loading team members...</span>
                                          </div>
                                      </td>
                                  </tr>
                              ) : filteredMemberRows.length === 0 ? (
                                  <tr>
                                      <td colSpan={tableColumnsCount} className={`${density.placeholderCell} text-center`}>
                                          <FiUser className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                          <p className="text-sm text-gray-500">No team members found</p>
                                      </td>
                                  </tr>
                              ) : (
                                  pagedMemberRows.map((row) => {
                                      const u = row.user
                                      const username = u?.username || formatName(u?.first_name, u?.last_name) || "N/A"
                                      const reportingPerson = formatName(u?.project_manager?.first_name, u?.project_manager?.last_name) || "N/A"

                                      const stickyCell = `sticky z-10 ${density.bodyCell} whitespace-nowrap bg-white border-r border-gray-200`

                                      return (
                                          <tr key={row.userId} className="hover:bg-gray-50">
                                              <td style={stickyColumns[0].style} className={stickyCell}>{u?.employee_code ?? "N/A"}</td>
                                              <td style={stickyColumns[1].style} className={stickyCell}>{username}</td>
                                              <td style={stickyColumns[2].style} className={stickyCell}>{row.userId}</td>
                                              <td style={stickyColumns[3].style} className={stickyCell}>{u?.designation?.name ?? "N/A"}</td>
                                              <td style={stickyColumns[4].style} className={stickyCell}>{u?.role?.name ?? "N/A"}</td>
                                              <td style={stickyColumns[5].style} className={stickyCell}>{getAssociationDisplayLabel(u?.association_type ?? "N/A")}</td>
                                              <td style={stickyColumns[6].style} className={stickyCell}>{reportingPerson}</td>
                                              <td className={`${density.bodyCell} whitespace-nowrap`}>{u?.state?.state_name ?? "N/A"}</td>
                                              <td className={`${density.bodyCell} whitespace-nowrap`}>{getUserDistrictName(u)}</td>
                                              <td style={{ minWidth: density.px(200) }} className={`${density.bodyCell} relative`}><ProjectNamesCell user={u} /></td>
                                              <td className={`${density.bodyCell} whitespace-nowrap`}>{getUserDepartmentOrProjectGroup(u)}</td>
                                              <td className={`${density.bodyCell} whitespace-nowrap`}>{u?.device_version ?? "N/A"}</td>
                                          </tr>
                                      )
                                  })
                              )}
                          </tbody>
                      </table>
                  </div>
                  <PaginationUtils
                      pagination={memberRowsPagination}
                      onPageChange={setMemberRowsPage}
                      onLimitChange={(limit) => {
                          setMemberRowsLimit(limit)
                          setMemberRowsPage(1)
                      }}
                  />
              </div>
          )
      }

      const MatrixView = () => {
          const detailColumnsCount = 11
          const countColumnsCount = matrixStatusMode === "work_status" ? 12 : 6 + ATTENDANCE_LEAVE_TYPE_OPTIONS.length
          const datesArray = Array.isArray(matrixDatesDesc) ? matrixDatesDesc : []
          const tableColumnsCount = detailColumnsCount + datesArray.length + countColumnsCount

          return (
              <div className="bg-white rounded shadow-sm border border-gray-200">
                  <div className="p-4 border-b border-gray-200">
                      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                          <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                  {currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                              </h3>
                              <div className="text-xs text-gray-500 mt-1">
                                  Range: {matrixRange.from} to {matrixRange.to} • Latest date shown first
                              </div>
                          </div>
                          <div className="flex items-center gap-3 flex-wrap">
                              {/* Work Status / HR Status toggle */}
                              <div className="flex items-center gap-2 bg-gray-100 rounded-full px-1 py-1">
                                  <button
                                      onClick={() => setMatrixStatusMode("work_status")}
                                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${matrixStatusMode === "work_status" ? "bg-white text-blue-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                  >
                                      Work Status
                                  </button>
                                  <button
                                      onClick={() => setMatrixStatusMode("hr_status")}
                                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${matrixStatusMode === "hr_status" ? "bg-white text-blue-800 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                                  >
                                      HR Status
                                  </button>
                              </div>
                              <button
                                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
                                  className="p-2 hover:bg-gray-100 rounded transition-colors"
                              >
                                  <FiChevronLeft className="w-5 h-5 text-gray-600" />
                              </button>
                              <button
                                  onClick={() => setCurrentDate(new Date())}
                                  className="px-3 py-1 text-sm bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors"
                              >
                                  Today
                              </button>
                              <button
                                  onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
                                  className="p-2 hover:bg-gray-100 rounded transition-colors"
                              >
                                  <FiChevronRight className="w-5 h-5 text-gray-600" />
                              </button>
                          </div>
                      </div>
                  </div>

                  <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-30">
                              <tr>
                                  {stickyColumns.map((column) => (
                                      <th
                                          key={column.key}
                                          style={column.style}
                                          className={`sticky z-30 ${density.headCell} text-left font-medium text-gray-600 uppercase bg-gray-50 border-r border-gray-200`}
                                      >
                                          {column.label}
                                      </th>
                                  ))}
                                  <th style={{ minWidth: density.px(120) }} className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Reporting State</th>
                                  <th style={{ minWidth: density.px(120) }} className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Reporting District</th>
                                  <th style={{ minWidth: density.px(200) }} className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Project Names</th>
                                  <th style={{ minWidth: density.px(150) }} className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Department/Project Group</th>
                                  {datesArray.map((dateStr) => {
                                      const d = parseYMDLocal(dateStr)
                                      const isSunday = d.getDay() === 0
                                      const label = d.toLocaleDateString("en-US", { day: "2-digit", month: "short" })
                                      return (
                                          <th
                                              key={dateStr}
                                              title={dateStr}
                                              style={{ minWidth: density.px(72) }}
                                              className={`${density.matrixHeadCell} text-center font-medium uppercase ${isSunday ? "bg-orange-100 text-orange-700" : "text-gray-600"}`}
                                          >
                                              {label}
                                          </th>
                                      )
                                  })}
                                  {matrixStatusMode === "work_status" ? (
                                      <>
                                          <th style={{ minWidth: density.px(60) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-green-100 text-green-700`}>Present</th>
                                          <th style={{ minWidth: density.px(52) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-green-100 text-green-700`}>Worked</th>
                                          <th style={{ minWidth: density.px(48) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-amber-100 text-amber-700`}>Late</th>
                                          <th style={{ minWidth: density.px(48) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-teal-100 text-teal-700`}>WFH</th>
                                          <th style={{ minWidth: density.px(48) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-yellow-100 text-yellow-700`}>Idle</th>
                                          <th style={{ minWidth: density.px(48) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-gray-200 text-gray-700`}>W/Off</th>
                                          <th style={{ minWidth: density.px(60) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-purple-100 text-purple-700`}>Holiday</th>
                                          <th style={{ minWidth: density.px(52) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-red-100 text-red-700`}>Absent</th>
                                          <th style={{ minWidth: density.px(96) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-gray-100 text-gray-500`}>Pending Approval</th>
                                          <th style={{ minWidth: density.px(84) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-slate-100 text-slate-600`}>Not Marked</th>
                                          <th style={{ minWidth: density.px(90) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-emerald-100 text-emerald-700`}>Working Days</th>
                                          <th style={{ minWidth: density.px(90) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-blue-100 text-blue-700`}>Leave Applied</th>
                                      </>
                                  ) : (
                                      <>
                                          <th style={{ minWidth: density.px(60) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-green-100 text-green-700`}>Present</th>
                                          <th style={{ minWidth: density.px(52) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-red-100 text-red-700`}>Absent</th>
                                          <th style={{ minWidth: density.px(96) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-gray-100 text-gray-500`}>Pending Approval</th>
                                          <th style={{ minWidth: density.px(84) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-slate-100 text-slate-600`}>Not Marked</th>
                                          <th style={{ minWidth: density.px(90) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-blue-100 text-blue-700`}>Leave Applied</th>
                                          {ATTENDANCE_LEAVE_TYPE_OPTIONS.map((option) => (
                                              <th key={option.value} style={{ minWidth: density.px(84) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-sky-100 text-sky-700`}>{option.label} Leave</th>
                                          ))}
                                          <th style={{ minWidth: density.px(90) }} className={`${density.matrixHeadCell} text-center font-medium uppercase bg-emerald-100 text-emerald-700`}>Working Days</th>
                                      </>
                                  )}
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                              {loading ? (
                                  <tr>
                                      <td colSpan={tableColumnsCount} className={`${density.placeholderCell} text-center`}>
                                          <div className="flex flex-col items-center">
                                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800 mb-3"></div>
                                              <span className="text-sm text-gray-600">Loading attendance matrix...</span>
                                          </div>
                                      </td>
                                  </tr>
                              ) : filteredMemberRows.length === 0 ? (
                                  <tr>
                                      <td colSpan={tableColumnsCount} className={`${density.placeholderCell} text-center`}>
                                          <FiCalendar className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                          <p className="text-sm text-gray-500">No attendance data found</p>
                                      </td>
                                  </tr>
                              ) : (
                                  pagedMemberRows.map((row) => {
                                      const u = row.user
                                      const username = u?.username || formatName(u?.first_name, u?.last_name) || "N/A"
                                      const reportingPerson = formatName(u?.project_manager?.first_name, u?.project_manager?.last_name) || "N/A"
                                      const stickyCell = `sticky z-10 ${density.bodyCell} whitespace-nowrap bg-white border-r border-gray-200`

                                      return (
                                          <tr key={row.userId} className="hover:bg-gray-50">
                                              <td style={stickyColumns[0].style} className={stickyCell}>{u?.employee_code ?? "N/A"}</td>
                                              <td style={stickyColumns[1].style} className={stickyCell}>{username}</td>
                                              <td style={stickyColumns[2].style} className={stickyCell}>{row.userId}</td>
                                              <td style={stickyColumns[3].style} className={stickyCell}>{u?.designation?.name ?? "N/A"}</td>
                                              <td style={stickyColumns[4].style} className={stickyCell}>{u?.role?.name ?? "N/A"}</td>
                                              <td style={stickyColumns[5].style} className={stickyCell}>{getAssociationDisplayLabel(u?.association_type ?? "N/A")}</td>
                                              <td style={stickyColumns[6].style} className={stickyCell}>{reportingPerson}</td>
                                              <td className={`${density.bodyCell} whitespace-nowrap`}>{u?.state?.state_name ?? "N/A"}</td>
                                              <td className={`${density.bodyCell} whitespace-nowrap`}>{getUserDistrictName(u)}</td>
                                              <td style={{ minWidth: density.px(200) }} className={`${density.bodyCell} relative`}><ProjectNamesCell user={u} /></td>
                                              <td className={`${density.bodyCell} whitespace-nowrap`}>{getUserDepartmentOrProjectGroup(u)}</td>
                                              {datesArray.map((dateStr) => {
                                                  const isHRCell = matrixStatusMode === "hr_status"
                                                  const rawStatus = row.statusesByDate[dateStr]
                                                  const status = rawStatus ?? (isHRCell ? HRStatus.NOT_MARKED : WorkStatus.NOT_MARKED)
                                                  const label = getMatrixCellLabel(status, isHRCell, row.leaveTypesByDate?.[dateStr])
                                                  const cellClasses = isHRCell
                                                      ? getHRStatusCellClasses(status as HRStatus)
                                                      : getWorkStatusCellClasses(status as WorkStatus)
                                                  const hasAttendanceRecord = Object.prototype.hasOwnProperty.call(row.statusesByDate, dateStr)
                                                  const isSundayCell = parseYMDLocal(dateStr).getDay() === 0
                                                  const actionKey = `${row.userId}-${dateStr}`
                                                  // A "Not Marked" cell has no record to open, but it is
                                                  // the clearest place to create one: the row is the
                                                  // employee and the column is the date, so clicking it
                                                  // opens Manual Mark with both already filled in.
                                                  // Only for a day Manual Mark would accept: never a
                                                  // future date, and today only while it is allowed.
                                                  const manualMarkBlockedReason =
                                                      !hasAttendanceRecord && canManualMarkAttendance
                                                          ? getManualMarkDateError(dateStr, manualMarkAllowToday)
                                                          : null
                                                  const canManualMarkCell =
                                                      !hasAttendanceRecord && canManualMarkAttendance && !manualMarkBlockedReason
                                                  const isInteractive =
                                                      (hasAttendanceRecord && canViewAttendanceDetails) ||
                                                      canManualMarkCell
                                                  const isBusy = matrixActionKey === actionKey
                                                  return (
                                                      <td
                                                          key={dateStr}
                                                          onClick={
                                                              !isInteractive
                                                                  ? undefined
                                                                  : canManualMarkCell
                                                                      ? () => handleMatrixNotMarkedClick(row.userId, dateStr)
                                                                      : () => handleMatrixCellClick(row.userId, dateStr)
                                                          }
                                                          title={
                                                              canManualMarkCell
                                                                  ? `${dateStr} • ${label} — click to mark attendance`
                                                                  : manualMarkBlockedReason
                                                                      ? `${dateStr} • ${label} — ${manualMarkBlockedReason}`
                                                                      : `${dateStr} • ${label}`
                                                          }
                                                          className={`${density.matrixDateCell} text-center font-semibold leading-tight whitespace-normal ${cellClasses} ${isSundayCell ? "ring-1 ring-inset ring-orange-300" : ""} ${isInteractive ? "cursor-pointer hover:ring-2 hover:ring-blue-300" : ""}`}
                                                      >
                                                          {isBusy ? "Loading..." : label}
                                                      </td>
                                                  )
                                              })}
                                              {matrixStatusMode === "work_status" ? (() => {
                                                  const c = countWorkMatrixRow(row.statusesByDate, datesArray)
                                                  return (
                                                      <>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-green-700 bg-green-50`}>{c.present || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-green-700 bg-green-50`}>{c.worked || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-amber-700 bg-amber-50`}>{c.late || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-teal-700 bg-teal-50`}>{c.wfh || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-yellow-700 bg-yellow-50`}>{c.idle || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-gray-700 bg-gray-100`}>{c.weekly_off || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-purple-700 bg-purple-50`}>{c.holiday || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-red-700 bg-red-50`}>{c.absent || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-gray-500 bg-gray-50`}>{c.pending || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-slate-600 bg-slate-50`}>{c.not_marked || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-emerald-700 bg-emerald-50`}>{c.working_days || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-blue-700 bg-blue-50`}>{c.leave || ""}</td>
                                                      </>
                                                  )
                                              })() : (() => {
                                                  const c = countHRMatrixRow(row.statusesByDate, row.workStatusesByDate, datesArray, row.leaveTypesByDate)
                                                  return (
                                                      <>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-green-700 bg-green-50`}>{c.present || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-red-700 bg-red-50`}>{c.absent || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-gray-500 bg-gray-50`}>{c.pending || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-slate-600 bg-slate-50`}>{c.not_marked || ""}</td>
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-blue-700 bg-blue-50`}>{c.leave_applied || ""}</td>
                                                          {ATTENDANCE_LEAVE_TYPE_OPTIONS.map((option) => (
                                                              <td key={option.value} className={`${density.matrixCountCell} text-center font-bold text-sky-700 bg-sky-50`}>{c.leave_by_type[option.value] || ""}</td>
                                                          ))}
                                                          <td className={`${density.matrixCountCell} text-center font-bold text-emerald-700 bg-emerald-50`}>{c.working_days || ""}</td>
                                                      </>
                                                  )
                                              })()}
                                          </tr>
                                      )
                                  })
                              )}
                          </tbody>
                      </table>
                  </div>
                  <PaginationUtils
                      pagination={memberRowsPagination}
                      onPageChange={setMemberRowsPage}
                      onLimitChange={(limit) => {
                          setMemberRowsLimit(limit)
                          setMemberRowsPage(1)
                      }}
                  />
              </div>
          )
      }

    const ListView = () => (
        <div className="bg-white rounded shadow-sm border border-gray-200">
            <div className="overflow-x-auto space-y-4">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            {!hideListActions && (
                                <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Actions</th>
                            )}
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                                <button
                                    type="button"
                                    onClick={() => handleSort("attendance_date")}
                                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                                >
                                    Date
                                    {getSortIcon("attendance_date")}
                                </button>
                            </th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Employee</th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Employee Code</th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Department</th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Projects</th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Circle</th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Reporting Person</th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Checkin Distance (km)</th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Distance Checkout (km)</th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                                <button
                                    type="button"
                                    onClick={() => handleSort("check_in_time")}
                                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                                >
                                    Check In
                                    {getSortIcon("check_in_time")}
                                </button>
                            </th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                                <button
                                    type="button"
                                    onClick={() => handleSort("check_out_time")}
                                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                                >
                                    Check Out
                                    {getSortIcon("check_out_time")}
                                </button>
                            </th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                                <button
                                    type="button"
                                    onClick={() => handleSort("worked_hours")}
                                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                                >
                                    Hours
                                    {getSortIcon("worked_hours")}
                                </button>
                            </th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                                <button
                                    type="button"
                                    onClick={() => handleSort("work_status")}
                                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                                >
                                    Status
                                    {getSortIcon("work_status")}
                                </button>
                            </th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                                <button
                                    type="button"
                                    onClick={() => handleSort("hr_status")}
                                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                                >
                                    HR Status
                                    {getSortIcon("hr_status")}
                                </button>
                            </th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>
                                <button
                                    type="button"
                                    onClick={() => handleSort("marking_type")}
                                    className={`flex items-center gap-2 text-gray-600 hover:text-blue-800 uppercase font-semibold ${density.isCompact ? "text-[10px]" : "text-xs"}`}
                                >
                                    Type
                                    {getSortIcon("marking_type")}
                                </button>
                            </th>
                            <th className={`${density.headCell} text-left font-medium text-gray-600 uppercase`}>Approver</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td colSpan={hideListActions ? 16 : 17} className={`${density.placeholderCell} text-center`}>
                                    <div className="flex flex-col items-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800 mb-3"></div>
                                        <span className="text-sm text-gray-600">Loading...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : attendanceData.length === 0 ? (
                            <tr>
                                <td colSpan={hideListActions ? 16 : 17} className={`${density.placeholderCell} text-center`}>
                                    <FiCalendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                    <p className="text-sm text-gray-500">No attendance records found</p>
                                </td>
                            </tr>
                        ) : (
                            attendanceData.map((record) => (
                                <tr key={record.id} className="hover:bg-gray-50">
                                    {!hideListActions && (
                                        <td className={`${density.bodyCell} whitespace-nowrap`}>
                                            <div className="flex items-center gap-2">
                                                {/* See isAttendanceApproved: the tick is only for
                                                    days still awaiting a decision. */}
                                                {canApproveAttendance && isAttendanceApproved(record) && (
                                                    <FiCheck
                                                        className="w-4 h-4 text-green-600"
                                                        title={`Approved by ${getAttendanceApproverName(record)}${record.approved_at ? ` on ${new Date(record.approved_at).toLocaleString()}` : ""}`}
                                                    />
                                                )}
                                                {canApproveAttendance && !isAttendanceApproved(record) && (
                                                    <button
                                                        onClick={() => handleApproveClick(record.id)}
                                                        className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded"
                                                        title="Approve Attendance"
                                                    >
                                                        <FiCheck className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {hasPermission('attendance', 'view') && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedAttendance(record)
                                                            setViewDetailsOpen(true)
                                                        }}
                                                        className="text-purple-600 hover:text-purple-800 p-1 hover:bg-purple-50 rounded"
                                                        title="View Details"
                                                    >
                                                        <FiList className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {hasPermission('attendance', 'calculate_payable') && (
                                                    <button
                                                        onClick={() => handleCalculateClick(record.user_id)}
                                                        className="text-green-600 hover:text-green-800 p-1 hover:bg-green-50 rounded"
                                                        title="Calculate Payable"
                                                    >
                                                        <FaCalculator className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                                        <div>{new Date(record.attendance_date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                                        <div className="text-xs text-gray-500">{new Date(record.attendance_date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long' })}</div>
                                    </td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                                        <div>
                                            <div className="text-sm font-medium text-gray-900">
                                                {record.user?.first_name} {record.user?.last_name}
                                            </div>
                                        </div>
                                    </td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>{record.user?.employee_code || "N/A"}</td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>{record.user?.department?.name || "N/A"}</td>
                                    <td style={{ minWidth: density.px(180) }} className={`${density.bodyCell} relative`}><ProjectNamesCell user={record.user} /></td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                                        {record.user?.state?.circle_name || record.user?.state?.state_name || "N/A"}
                                    </td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                                        {record.user?.project_manager
                                            ? `${record.user.project_manager.first_name ?? ""} ${record.user.project_manager.last_name ?? ""}`.trim() || "N/A"
                                            : "N/A"}
                                    </td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                                        {typeof record.distance_from_reporting === "number"
                                            ? (record.distance_from_reporting / 1000).toFixed(2)
                                            : "N/A"}
                                    </td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                                        {typeof record.check_out_distance_from_reporting === "number"
                                            ? (record.check_out_distance_from_reporting / 1000).toFixed(2)
                                            : "N/A"}
                                    </td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                                        {record.check_in_time ? new Date(record.check_in_time).toLocaleTimeString() : "N/A"}
                                    </td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                                        {record.check_out_time ? new Date(record.check_out_time).toLocaleTimeString() : "N/A"}
                                    </td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`} title={record.is_auto_checkout ? "Auto checkout - the user did not check out" : undefined}>{record.is_auto_checkout ? "NA" : record.worked_hours || "N/A"}</td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                                        <div className="flex items-center gap-1">
                                            {getWorkStatusBadge(record.work_status, record.leave_type)}
                                            <VerificationBadge record={record} />
                                        </div>
                                    </td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                                        <span
                                            className={`px-2 py-1 rounded text-xs font-medium ${getDisplayHRStatusBadgeClasses(record.hr_status, record.work_status)}`}
                                        >
                                            {getDisplayHRStatusLabel(record.hr_status, record.work_status)}
                                        </span>
                                    </td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>
                                        <span
                                            className={`px-2 py-1 rounded text-xs font-medium ${record.marking_type === "auto" ? "bg-blue-100 text-blue-900" : "bg-orange-100 text-orange-700"
                                                }`}
                                        >
                                            {record.marking_type}
                                        </span>
                                    </td>
                                    <td className={`${density.bodyCell} whitespace-nowrap`}>{getAttendanceApproverName(record)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <PaginationUtils pagination={pagination} onPageChange={handlePageChange} onLimitChange={handleLimitChange} />
        </div>
    )

    return (
        <>
            <div className="space-y-6">
                {/* Header */}
                <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">{pageTitle}</h1>
                            <p className="text-sm text-gray-600 mt-1">{pageDescription}</p>
                        </div>

                        {showTabs && (
                            <div className="flex flex-col items-center">
                                <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
                                    <button
                                        onClick={() => {
                                            if (activeTab !== "direct") {
                                                setActiveTab("direct")
                                                setPagination((prev) => ({ ...prev, page: 1 }))
                                            }
                                        }}
                                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${activeTab === "direct"
                                            ? "bg-white text-blue-800 shadow-sm"
                                            : "text-gray-500 hover:text-gray-700"
                                            }`}
                                    >
                                        Direct Team
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (activeTab !== "project") {
                                                setActiveTab("project")
                                                setPagination((prev) => ({ ...prev, page: 1 }))
                                            }
                                        }}
                                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${activeTab === "project"
                                            ? "bg-white text-blue-800 shadow-sm"
                                            : "text-gray-500 hover:text-gray-700"
                                            }`}
                                    >
                                        All Team
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center gap-2">
                            {canManualMarkAttendance && activeTab === "direct" && !isMembersVariant && (
                                <button
                                    onClick={() => setManualMarkModalOpen(true)}
                                    className="px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors text-sm font-medium flex items-center gap-2"
                                >
                                    <FiEdit className="w-4 h-4" />
                                    Manual Mark
                                </button>
                            )}
                            {canBulkApproveAttendance && !isMembersVariant && (
                                <button onClick={() => setBulkApproveMonthModalOpen(true)} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors text-sm font-medium flex items-center gap-2">
                                    <FiCheck className="w-4 h-4" /> Bulk Approve Month
                                </button>
                            )}
                            {canExport && (
                                <button
                                    onClick={fetchExportData}
                                    disabled={exporting}
                                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2 disabled:bg-green-400 disabled:cursor-not-allowed"
                                >
                                    <FaFileExcel className="w-4 h-4" />
                                    {exporting ? "Preparing..." : "Export"}
                                </button>
                            )}
                            {viewMode !== "calendar" && viewMode !== "map" && (
                                <DensityToggle density={density} />
                            )}
                            <div className="flex items-center gap-2 bg-gray-100 rounded p-1">
                                {variant !== 'members' && (
                                    <button
                                        onClick={() => setViewMode("matrix")}
                                        className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${viewMode === "matrix" ? "bg-white text-blue-800 shadow-sm" : "text-gray-600"
                                            }`}
                                    >
                                        <FiGrid className="w-4 h-4" />
                                        Matrix
                                    </button>
                                )}
                                <button
                                    onClick={() => setViewMode("list")}
                                    className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${viewMode === "list" ? "bg-white text-blue-800 shadow-sm" : "text-gray-600"
                                        }`}
                                >
                                    <FiList className="w-4 h-4" />
                                    List
                                </button>
                                {variant !== 'members' && (
                                    <button
                                        onClick={() => setViewMode("calendar")}
                                        className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${viewMode === "calendar" ? "bg-white text-blue-800 shadow-sm" : "text-gray-600"
                                            }`}
                                    >
                                        <FiCalendar className="w-4 h-4" />
                                        Calendar
                                    </button>
                                )}
                                {variant !== 'members' && canViewAttendanceMap && (
                                    <button
                                        onClick={() => setViewMode("map")}
                                        className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${viewMode === "map" ? "bg-white text-blue-800 shadow-sm" : "text-gray-600"
                                            }`}
                                    >
                                        <FiMap className="w-4 h-4" />
                                        Map
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="relative w-full max-w-md">
                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search by employee name, code, or email..."
                                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-blue-800 transition-colors text-sm"
                                    value={searchInput}
                                    onChange={(e) => {
                                        const value = e.target.value
                                        setPagination((prev) => ({ ...prev, page: 1 }))
                                        setSearchInput(value)
                                    }}
                                />
                            </div>
                            <button
                                onClick={() => setIsFilterModalOpen(true)}
                                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors text-sm font-medium flex items-center gap-2"
                            >
                                <FiFilter className="w-4 h-4" />
                                Filter
                                {activeFilterCount > 0 && (
                                    <span className="bg-blue-800 text-white text-xs px-2 py-0.5 rounded-full">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </button>
                            {activeFilterCount > 0 && (
                                <button
                                    onClick={handleResetFilters}
                                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                                >
                                    <FiRefreshCw className="w-4 h-4" />
                                    Clear
                                </button>
                            )}
                        </div>
                        {viewMode !== "map" && (
                            <div className="text-sm text-gray-500">
                                {viewMode === "matrix" || variant === "members"
                                    ? `${uniqueUserCount} member(s) found`
                                    : `${pagination.total_count} record(s) found`}
                            </div>
                        )}
                    </div>
                </div>

                {/* Content */}
                {viewMode === "calendar" ? (
                    <CalendarView />
                ) : viewMode === "matrix" ? (
                    <MatrixView />
                ) : viewMode === "map" ? (
                    <div className="bg-white rounded shadow-sm border border-gray-200">
                        {/* Map controls */}
                        <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
                            <div className="flex items-center gap-2">
                                <FiCalendar className="w-4 h-4 text-gray-500" />
                                <label className="text-sm font-medium text-gray-700">Date:</label>
                                <input
                                    type="date"
                                    value={mapDate}
                                    onChange={e => setMapDate(e.target.value)}
                                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="flex items-center gap-1 ml-auto">
                                <FiLayers className="w-4 h-4 text-gray-500 mr-1" />
                                <button
                                    onClick={() => setShowCheckInLayer(v => !v)}
                                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${showCheckInLayer ? "bg-green-600 text-white border-green-600" : "bg-white text-gray-600 border-gray-300"}`}
                                >
                                    <span className="w-2.5 h-2.5 rounded-full inline-block bg-green-500"></span>
                                    Check-in
                                </button>
                                <button
                                    onClick={() => setShowReportingLayer(v => !v)}
                                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${showReportingLayer ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-600 border-gray-300"}`}
                                >
                                    <span className="w-2.5 h-2.5 rounded-full inline-block bg-purple-500"></span>
                                    Reporting
                                </button>
                                <button
                                    onClick={() => setShowCheckOutLayer(v => !v)}
                                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${showCheckOutLayer ? "bg-rose-600 text-white border-rose-600" : "bg-white text-gray-600 border-gray-300"}`}
                                >
                                    <span className="w-2.5 h-2.5 rounded-full inline-block bg-rose-500"></span>
                                    Check-out
                                </button>
                                {mapLoading && <span className="text-xs text-gray-500 ml-2">Loading...</span>}
                                {!mapLoading && <span className="text-xs text-gray-500 ml-2">{mapAttendanceData.length} record(s)</span>}
                            </div>
                        </div>
                        <div ref={mapContainerRef} className="relative" style={{ height: isMapFullscreen ? "100vh" : "560px", width: "100%" }}>
                            <button
                                onClick={toggleMapFullscreen}
                                title={isMapFullscreen ? "Exit fullscreen" : "Fullscreen"}
                                className="absolute top-2 right-2 z-[400] bg-white border border-gray-300 rounded p-1.5 shadow hover:bg-gray-50"
                            >
                                {isMapFullscreen ? <FiMinimize2 className="w-4 h-4 text-gray-700" /> : <FiMaximize2 className="w-4 h-4 text-gray-700" />}
                            </button>
                            <div ref={mapDivRef} style={{ height: "100%", width: "100%" }} />
                        </div>
                    </div>
                ) : variant === "members" ? (
                    <MembersListView />
                ) : (
                    <ListView />
                )}
            </div>

            {/* Modals */}
            <AttendanceDateOffcanvas
                isOpen={dateOffcanvasOpen}
                onClose={() => setDateOffcanvasOpen(false)}
                date={selectedDate}
                attendances={selectedDateAttendances}
                onApprove={handleApproveClick}
                readOnly={readOnly}
                onManualMark={() => {
                    setManualMarkModalOpen(true)
                    setDateOffcanvasOpen(false)
                }}
                onView={(attendance) => {
                    setSelectedAttendance(attendance)
                    setViewDetailsOpen(true)
                    setDateOffcanvasOpen(false)
                }}
            />

            <ApproveModal
                isOpen={approveModalOpen}
                onClose={() => setApproveModalOpen(false)}
                attendance={selectedAttendance}
                onSuccess={fetchAttendance}
            />

            <ManualMarkModal
                isOpen={manualMarkModalOpen}
                onClose={() => {
                    setManualMarkModalOpen(false)
                    setManualMarkPreset(null)
                }}
                selectedDate={
                    manualMarkPreset?.date ??
                    (selectedDate ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}` : undefined)
                }
                presetUserId={manualMarkPreset?.userId}
                allowToday={manualMarkAllowToday}
                onSuccess={fetchAttendance}
            />

            <BulkApproveMonthModal
                isOpen={bulkApproveMonthModalOpen}
                onClose={() => setBulkApproveMonthModalOpen(false)}
                currentDate={currentDate}
                onSuccess={fetchAttendance}
            />

            <CalculatePayableModal
                isOpen={calculateModalOpen}
                onClose={() => setCalculateModalOpen(false)}
                userId={selectedUserId}
            />

            <ViewDetailsOffcanvas
                isOpen={viewDetailsOpen}
                onClose={() => setViewDetailsOpen(false)}
                attendance={selectedAttendance}
            />

            <ExcelDownload
                isOpen={showExcelModal}
                onClose={() => setShowExcelModal(false)}
                data={exportData}
                filename={isMembersVariant ? "team_members" : "attendance"}
                title={isMembersVariant ? "Export Team Members" : "Export Attendance Data"}
                description={isMembersVariant
                    ? "Download the filtered team-member records with all available details"
                    : "Download the filtered attendance records with all available details"}
            />

            <FilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                filters={filters}
                onFiltersChange={setFilters}
                onApply={() => fetchAttendance(pagination.limit)}
                onReset={handleResetFilters}
                memberOnly={isMembersVariant}
                activeTab={activeTab}
                projectOptions={projectFilterOptions}
                departmentGroupOptions={departmentGroupOptions}
                roleOptions={roleOptions}
            />
        </>
    )
}
const AttendanceManagementList: React.FC = () => {
    return <AttendanceManagement showTabs={false} initialTab="direct" defaultViewMode="list" />
}

export default AttendanceManagementList
